// src/Components/EventWizard/MinimalDashboard.jsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import StudioCamPresetPanel from './StudioCamPresetPanel';
import MinimalMpdCamToggler from './MinimalMpdCamToggler';
import LivekitEventPanel from './LivekitEventPanel';
import { POLL_MS, confirmDanger } from './lib/ui';
import { getDashMediaPlayerLiveSettings } from '@into-reality/player';
import { authFetch } from '../../utils/authFetch';

const API_BASE =
  (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL.replace(/\/+$/, '')) ||
  'http://localhost:4000';

const COLORS = { ok: '#4caf50', err: '#f44336', idle: '#9e9e9e', warn: '#ff9800', neutral: '#3f51b5' };

// Monitor polling is one request per camera through the backend proxy to each
// EC2, so it runs slower than POLL_MS (2s) — alignment moves in whole segments
// (seconds), and a 6-camera event at 2s would mean 3 req/s of monitoring traffic.
const MONITOR_POLL_MS = 5000;

// |offsetSegments| at or above this is worth an amber flag: each segment the
// camera runs ahead of the event clock is ~1s of extra latency for the viewer.
const OFFSET_WARN_SEGS = 2;

// The local `styles` object below only carries `badge`, so buttons style inline.
const driftBtn = (bg, enabled = true) => ({
  backgroundColor: bg,
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  padding: '8px 16px',
  fontSize: 14,
  fontWeight: 600,
  cursor: enabled ? 'pointer' : 'not-allowed',
  opacity: enabled ? 1 : 0.5,
});

function detectRoleFromInst(inst) {
  const raw = String(inst?.role || inst?.kind || inst?.type || '').toLowerCase();
  if (raw === 'audio' || raw === 'video') return raw;
  const name = String(inst?.camName || '').toLowerCase();
  if (name.includes('mic') || name.includes('audio') || name.includes('voice')) return 'audio';
  return 'video';
}

const BADGE_MIN_WIDTH = 88;   // consistent width across pills
const BADGE_HEIGHT = 26;      // consistent height
const styles = {
  badge: (bg) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '0 0px',
    minWidth: BADGE_MIN_WIDTH,
    height: BADGE_HEIGHT,
    borderRadius: BADGE_HEIGHT / 2,
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 18,
    backgroundColor: bg,
    lineHeight: 1,
    whiteSpace: 'nowrap',
  }),
};
const Badge = ({ ok, on = COLORS.ok, off = COLORS.err, labelOk = 'OK', labelOff = 'OFF' }) => (
  <span style={styles.badge(ok ? on : off)}>{ok ? labelOk : labelOff}</span>
);
const Pill = ({ color = COLORS.warn, children }) => <span style={styles.badge(color)}>{children}</span>;

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// --- helpers to ensure commands are saved & uploaded ---
const detectRole = (inst) => {
  const raw = String(inst?.role || inst?.kind || inst?.type || '').toLowerCase();
  if (raw === 'audio' || raw === 'video') return raw;
  const name = String(inst?.camName || '').toLowerCase();
  if (name.includes('mic') || name.includes('audio') || name.includes('voice')) return 'audio';
  return 'video';
};
const fetchEventCommandMap = async (api, evt) => {
  const resp = await api.getEvent(evt);
  const arr = resp?.data?.commands || [];
  const map = {};
  for (const c of arr) {
    if (c.instanceId && typeof c.command === 'string' && c.command.trim()) {
      map[c.instanceId] = c.command.trim();
    }
  }
  return map;
};
const fetchRoleDefaults = async (api) => {
  const pr = await api.listPresets();
  const presets = pr?.data?.presets || [];
  const defVideo = presets.find((p) => p.isDefault && (p.role || p.type) === 'video') || null;
  const defAudio = presets.find((p) => p.isDefault && (p.role || p.type) === 'audio') || null;
  return { video: defVideo?.command || null, audio: defAudio?.command || null };
};
const ensureCommandsUploaded = async (api, setMessage, evt, cams) => {
  const [cmdMap, roleDefaults] = await Promise.all([
    fetchEventCommandMap(api, evt),
    fetchRoleDefaults(api),
  ]);
  for (const inst of cams) {
    const role = detectRole(inst);
    const selected = cmdMap[inst.instanceId];
    const desired = selected || (role === 'audio' ? roleDefaults.audio : roleDefaults.video);
    if (!desired) {
      setMessage?.(`No saved preset or role default in DB for ${inst.camName} (${role}); skipping this cam.`);
      continue;
    }
    try {
      await api.saveEventCmd(evt, { instanceId: inst.instanceId, command: desired, role });
      await api.updateNodeScript(inst.instanceId, desired);
    } catch (e) {
      setMessage?.(`Warning: could not push FFmpeg to ${inst.camName}: ${e.message}`);
    }
  }
};

// Pushes each cam's declared ingest profile down to its instance. Instances are
// terminated and relaunched per event, and the controller's ingest-config.json dies
// with the AMI — so the DB re-seeds it here, right before /ffmpeg/prepare, which is
// where syncSlate and syncNormalizer read it. A cam with nothing declared is left
// alone: the instance keeps whatever it already had.
const ensureIngestProfilesUploaded = async (api, setMessage, evt, cams) => {
  let profiles = {};
  let normalizerQuality = null;
  try {
    const r = await api.getIngestProfiles(evt);
    profiles = r.data?.ingestProfiles || {};
    normalizerQuality = r.data?.normalizerQuality || null;
  } catch (e) {
    setMessage?.(`Warning: could not read ingest profiles: ${e.message}`);
    return;
  }
  // Event-level normalizer quality override rides in the same per-instance
  // push; the controller applies it when composing normalizer.sh at start.
  const nq = normalizerQuality || {};
  const boolKey = v => (typeof v === 'boolean' ? (v ? 1 : 0) : '');
  const normKeys = {
    normalizerPreset:      nq.preset || '',
    normalizerTemporalAq:  boolKey(nq.temporalAq),
    normalizerSpatialAq:   boolKey(nq.spatialAq),
    normalizerAqStrength:  nq.aqStrength ?? '',
    normalizerBitrateMbps: nq.bitrateMbps ?? '',
  };
  // Push + VERIFY per instance: the controller's response echoes back the
  // declared config it actually persisted, so a divergence (network hiccup,
  // older controller ignoring the normalizer keys, validation reject) gets a
  // name instead of silently starting the event with a stale config.
  const failed = [];
  for (const inst of cams) {
    const p = profiles[inst.camName] || {};
    if (!profiles[inst.camName] && !normalizerQuality) continue;
    try {
      const r = await api.setIngestConfig(inst.instanceId, {
        width:  p.width  ?? '',
        height: p.height ?? '',
        codec:  p.codec  ?? '',
        kbps:   p.kbps   ?? '',
        ...normKeys,
      });
      if (normalizerQuality) {
        const applied = r.data?.declared?.normalizer || {};
        const wantPreset = nq.preset || null;
        const gotPreset  = applied.preset || null;
        if (wantPreset !== gotPreset) {
          failed.push(`${inst.camName} (normalizer preset: wanted ${wantPreset || 'default'}, instance has ${gotPreset || 'default'})`);
        }
      }
    } catch (e) {
      failed.push(`${inst.camName} (push failed: ${e.response?.data?.errors?.join('; ') || e.message})`);
    }
  }
  if (failed.length) {
    setMessage?.(`⚠ Ingest config NOT verified on: ${failed.join(' · ')} — these cams may run with a stale normalizer config.`);
  }
};

const SPEED_OK_THRESHOLD = 0.95;
function renderSpeedPill(val) {
  // Accept numbers or strings like "0.98" / "0.98x"
  const num = typeof val === 'number' ? val : parseFloat(val);
  if (!Number.isFinite(num)) {
    return <span style={styles.badge(COLORS.idle)} title="No speed">-</span>;
  }
  const color = num > SPEED_OK_THRESHOLD ? COLORS.ok : COLORS.warn;
  const label = `${num.toFixed(2)}x`;
  return <span style={styles.badge(color)} title={`Speed: ${label}`}>{label}</span>;
}

export default function MinimalDashboard({
  grouped,
  rtmpOk,
  ingestOk,
  ingestKbps,
  ffmpegStat,
  speedMap,
  setSpeedMap = () => {},
  onRefresh,
  setMessage,
  onGenerateAndUploadMpd,
  api,
  pendingLaunchesByEvent = {},
  presets = [],
  manifestText = {},
  manifestMeta = {},
  showManifest = {},
  setShowManifest = () => {},
  fetchManifestForEvent,
  onUploadTemplateMpd,
  setManifestText = () => {},
  // Advanced mode props
  advancedMode = false,
  cmdFromDb = {},
  onPresetsReload,
  fetchFull,
  backendUrl = '',
  manifestText8K = {},
  manifestTextFull = {},
  setManifestText8K = () => {},
  setManifestTextFull = () => {},
  saveAndPushMpd,
  dashSettingsContent = {},
  setDashSettingsContent,
  saveDashSettings,
}) {
  const [selectedCamInstanceId, setSelectedCamInstanceId] = useState(null);
  const [showPresetPanel, setShowPresetPanel] = useState(false);
  const [presetPanelLoading, setPresetPanelLoading] = useState(false);

  // Advanced mode: expanded row state
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [expandedTab, setExpandedTab] = useState('preset');
  const [editCmd, setEditCmd] = useState('');
  const [logText, setLogText] = useState('');
  const [advPresetId, setAdvPresetId] = useState('');
  const [advPresetName, setAdvPresetName] = useState('');
  const [advPresetDesc, setAdvPresetDesc] = useState('');

  // Per-camera selection for Start Event (instanceId → boolean)
  const [startSelection, setStartSelection] = useState({});

  // Manifest editor panel state
  const [mpdOpen, setMpdOpen] = useState(false);
  const [mpdTab, setMpdTab] = useState('main'); // 'main' | '8k' | 'full'
  const [mpdEdit, setMpdEdit] = useState({});   // { [evt]: text }
  const [mpdEdit8K, setMpdEdit8K] = useState({});
  const [mpdEditFull, setMpdEditFull] = useState({});
  const [mpdSaving, setMpdSaving] = useState(false);

  // Dash Settings panel state
  const [dashSettingsOpen, setDashSettingsOpen] = useState(false);
  const [dashSettingsEdit, setDashSettingsEdit] = useState({});
  const [dashSettingsSaving, setDashSettingsSaving] = useState(false);

  // Player Settings panel state

  // Server Config panel state
  const [cfgOpen, setCfgOpen] = useState(false);
  const [cfgTab, setCfgTab] = useState('srs'); // 'srs' | 'controller'
  const [cfgText, setCfgText] = useState('');
  const [cfgLoading, setCfgLoading] = useState(false);
  const [cfgResults, setCfgResults] = useState(null); // [{ instanceId, camName, ... }]

  // Failover toggle, normalizer quality and slate logo panels removed (IR-198):
  // the recovery controller retired the slate/normalizer chain and the failover
  // proxy — an ingest drop is ridden out by per-camera supervision and a
  // -start_number relaunch on the same timeline.

  // Declared ingest profiles: the rig's real parameters per camera, so slate +
  // normalizer can be sized before the camera publishes (that's what allows starting
  // an event on the logo alone). Persisted in Mongo keyed by camName — instances are
  // relaunched per event and their local ingest-config.json dies with the AMI.
  // Latency target: what the manifest tells the player, and how far it could come
  // down given this session's camera offsets.
  const [latencyState, setLatencyState] = useState(null);   // { targetMs, info }
  const [latencyDraft, setLatencyDraft] = useState('');
  const [latencySaving, setLatencySaving] = useState(false);

  const [ingestDrafts, setIngestDrafts] = useState({});     // camName → editable {width,height,codec,kbps}
  const [ingestSuggested, setIngestSuggested] = useState({}); // camName → prefill derived from last run's MPD
  const [ingestSaving, setIngestSaving] = useState(false);
  const [ingestResult, setIngestResult] = useState(null);
  const [ingestState, setIngestState] = useState({});       // instanceId → live {declared, measured, effective, mismatch}

  // Editable cam labels (persisted in DB)
  const [camLabels, setCamLabels] = useState({});
  const labelTimers = useRef({});

  // Load labels from DB when events change
  useEffect(() => {
    if (!grouped || !api) return;
    Object.keys(grouped).forEach(async (evt) => {
      try {
        const r = await api.getCamLabels(evt);
        if (r.data?.success) {
          setCamLabels(prev => ({ ...prev, ...Object.fromEntries(Object.entries(r.data.camLabels || {})) }));
        }
      } catch {}
    });
  }, [grouped, api]);

  const updateCamLabel = (evt, instanceId, label) => {
    setCamLabels(prev => ({ ...prev, [instanceId]: label }));
    // Debounce save to DB (500ms)
    clearTimeout(labelTimers.current[instanceId]);
    labelTimers.current[instanceId] = setTimeout(() => {
      api.saveCamLabel(evt, instanceId, label).catch(() => {});
    }, 500);
  };

  // Editable content offsets per camera (persisted in DB, keyed by camName)
  const [contentOffsets, setContentOffsets] = useState({});
  const offsetTimers = useRef({});

  // Load content offsets from DB when events change
  useEffect(() => {
    if (!grouped || !api) return;
    Object.keys(grouped).forEach(async (evt) => {
      try {
        const r = await api.getContentOffsets(evt);
        if (r.data?.success) {
          // Replace this event's entries instead of merging: a plain merge kept
          // stale values alive after the session reset cleared them in the DB
          // (ghost offsets from the previous session shown in the panel,
          // 2026-10-01). Drop this event's cams first, then apply what the DB
          // actually has.
          setContentOffsets(prev => {
            const next = { ...prev };
            for (const inst of grouped[evt] || []) {
              const k = (inst.camName || '').toLowerCase();
              if (k) delete next[k];
            }
            for (const [k, v] of Object.entries(r.data.contentOffsets || {})) next[k] = v;
            return next;
          });
        }
      } catch {}
    });
  }, [grouped, api]);

  const updateContentOffset = (evt, camName, value) => {
    const key = (camName || '').toLowerCase();
    setContentOffsets(prev => ({ ...prev, [key]: value }));
    clearTimeout(offsetTimers.current[key]);
    offsetTimers.current[key] = setTimeout(() => {
      api.saveContentOffset(evt, camName, value).catch(() => {});
    }, 500);
  };

  // 15s grace window per camera (avoid "Failed" within the window)
  const [graceUntilByInstanceId, setGraceUntilByInstanceId] = useState({});
  const isInGrace = (id) => (graceUntilByInstanceId[id] || 0) > Date.now();

  // Abort mechanism for autoStartEvent
  const startAbortRef = useRef(false);
  const [startingEvent, setStartingEvent] = useState(false);

  // MPD fragment enabled states: { "evt/camName/role" -> boolean }
  const [fragStates, setFragStates] = useState({});

  const refreshFragStates = async (evt) => {
    try {
      const r = await api.getEvent(evt);
      const frags = r.data?.mpdFragments || [];
      setFragStates(prev => {
        const next = { ...prev };
        for (const k of Object.keys(next)) {
          if (k.startsWith(`${evt}/`)) delete next[k];
        }
        for (const f of frags) {
          const key = `${evt}/${(f.camName || '').toLowerCase()}/${f.role}`;
          next[key] = f.enabled !== false;
        }
        return next;
      });
    } catch {}
  };

  const getFragEnabled = (evt, camName, role) => {
    const key = `${evt}/${(camName || '').toLowerCase()}/${role}`;
    return fragStates[key];
  };

  // === NEW: First segment times (per event) ===
  const [firstSegByEvent, setFirstSegByEvent] = useState({}); // { [evt]: { loadedAt, rows: [] } }
  const [openFirstSegEvt, setOpenFirstSegEvt] = useState(null);
  const [firstSegLoadingEvt, setFirstSegLoadingEvt] = useState(null);

  // === Elastic IPs (advanced): fixed camera ips that follow the fresh
  // instances of each event, associated from Studio instead of the AWS console.
  const [eips, setEips] = useState(null);          // null = not loaded
  const [eipSel, setEipSel] = useState({});        // instanceId → allocationId
  const [eipBusy, setEipBusy] = useState(false);
  const loadEips = async () => {
    try {
      const r = await api.listElasticIps();
      setEips(r.data?.addresses || []);
    } catch (e) {
      setMessage?.(`Could not list Elastic IPs: ${e.response?.data?.message || e.message}`);
      setEips([]);
    }
  };
  useEffect(() => {
    if (advancedMode && eips === null) loadEips();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedMode]);
  const associateEip = async (inst) => {
    const allocationId = eipSel[inst.instanceId];
    if (!allocationId) return;
    const eip = (eips || []).find(a => a.allocationId === allocationId);
    if (eip?.instanceId && eip.instanceId !== inst.instanceId) {
      if (!confirmDanger(
        `${eip.name ? `"${eip.name}" (${eip.publicIp})` : eip.publicIp} is currently attached to "${eip.instanceName}".\n\n` +
        `Re-associating will TAKE it from that instance (its public ip changes and ` +
        `anything pointing at it goes dark). Continue?`
      )) return;
    }
    setEipBusy(true);
    try {
      await api.associateEip(inst.instanceId, allocationId);
      setMessage?.(`${eip?.publicIp || 'Elastic IP'} associated to ${inst.camName}. Refreshing…`);
      await loadEips();
      onRefresh?.(); // the instance's public ip just changed — reload the list
    } catch (e) {
      setMessage?.(`Associate failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setEipBusy(false);
    }
  };

  // === LiveKit (WHIP) path — parallel to DASH, per event ===
  const [streamingModeByEvent, setStreamingModeByEvent] = useState({}); // { [evt]: 'dash'|'livekit' }
  const [livekitIngressByEvent, setLivekitIngressByEvent] = useState({}); // { [evt]: { [camName]: {...} } }
  const [livekitParticipantsByEvent, setLivekitParticipantsByEvent] = useState({}); // { [evt]: { [identity]: participant } }

  // `silent` distinguishes the 5s auto-poll (a transient failure just retries
  // next tick — surfacing it would spam the message bar) from a user-initiated
  // refresh, where a failure must be visible or it looks identical to "no
  // ingress created yet".
  const refreshLivekitIngress = async (evt, { silent = false } = {}) => {
    try {
      const r = await api.getLivekitIngress(evt);
      setLivekitIngressByEvent(prev => ({ ...prev, [evt]: r.data?.ingress || {} }));
    } catch (e) {
      if (!silent) setMessage?.(`LiveKit ingress status check failed for "${evt}": ${e.response?.data?.message || e.message}`);
    }
  };

  // The real confirmation a camera is live in the room — not just that the
  // ingress connected. Same check as `GET /api/rooms/:name/participants`.
  const refreshLivekitParticipants = async (evt, { silent = false } = {}) => {
    try {
      const r = await api.getLivekitParticipants(evt);
      const byIdentity = Object.fromEntries((r.data?.participants || []).map(p => [p.identity, p]));
      setLivekitParticipantsByEvent(prev => ({ ...prev, [evt]: byIdentity }));
    } catch (e) {
      if (!silent) setMessage?.(`LiveKit participants check failed for "${evt}": ${e.response?.data?.message || e.message}`);
    }
  };

  // Auto-poll for events in LiveKit mode: ingress state (publishing/error),
  // in-room participants and viewer count refresh on their own, matching how
  // the DASH badges already behave. Serialized, visibility-aware; reads the
  // mode map through a ref so re-renders don't tear the timer down.
  const LIVEKIT_POLL_MS = 5000;
  const streamingModeRef = useRef(streamingModeByEvent);
  useEffect(() => { streamingModeRef.current = streamingModeByEvent; }, [streamingModeByEvent]);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      if (document.visibilityState === 'visible') {
        const evts = Object.entries(streamingModeRef.current || {})
          .filter(([, m]) => m === 'livekit')
          .map(([e]) => e);
        for (const evt of evts) {
          if (cancelled) return;
          await refreshLivekitIngress(evt, { silent: true });
          await refreshLivekitParticipants(evt, { silent: true });
        }
      }
      if (!cancelled) setTimeout(tick, LIVEKIT_POLL_MS);
    };
    const t = setTimeout(tick, LIVEKIT_POLL_MS);
    return () => { cancelled = true; clearTimeout(t); };
    // refresh helpers only close over `api` (memoised) and setters — stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setEventMode = async (evt, mode) => {
    setStreamingModeByEvent(prev => ({ ...prev, [evt]: mode })); // optimistic
    try {
      await api.setStreamingMode(evt, mode);
      if (mode === 'livekit') {
        refreshLivekitIngress(evt);
        refreshLivekitParticipants(evt);
      }
    } catch (e) {
      setMessage?.(`Could not switch mode: ${e.response?.data?.message || e.message}`);
    }
  };

  // === Camera monitor panel (per event) — GET /monitor on each cam (IR-198) ===
  // One camera at a time is useless for sync: what matters is how far the
  // cameras are from EACH OTHER, and that only shows up side by side. The most
  // valuable check is clock.epochMs: every camera of the event must carry the
  // SAME value to the millisecond — any difference IS the desynchronisation.
  const [monitorByEvent, setMonitorByEvent] = useState({});   // { [evt]: { loadedAt, rows: [] } }
  const [openMonitorEvt, setOpenMonitorEvt] = useState(null);
  const [monitorLoadingEvt, setMonitorLoadingEvt] = useState(null);

  // === ⚡ Low-latency toggle per event ===
  // Governs manifest (ATO 0.8 + complete=false on rebuild, target floor 1200ms)
  // and the LL player preset in dashSettings. Transport stays in the ffmpeg
  // preset URL (8091 receiver / 8090 DAV) — not part of this toggle.
  // First click loads the stored state (a blind click must never flip it).
  const [llByEvt, setLlByEvt] = useState({});   // { [evt]: bool | null(cargando) | undefined }
  // Evento "en marcha" = goEpochMs acuñado en BBDD y aún no finalizado con Stop
  // Event. NO se infiere de los encoders: si todas las cams caen a la vez, el
  // evento SIGUE en marcha (el player ya gestiona el mensaje al viewer) y los
  // ⏹/▶ por cámara deben seguir disponibles para recuperarlas.
  const [liveByEvt, setLiveByEvt] = useState({}); // { [evt]: bool | undefined(sin cargar) }
  // Versión de manifest de la sesión actual, para mostrar la URL REAL (no la
  // fija por convención). null => evento no arrancado todavía.
  const [mfVersionByEvt, setMfVersionByEvt] = useState({}); // { [evt]: string | null }
  const toggleLowLatency = async (evt) => {
    let cur = llByEvt[evt];
    if (cur === undefined || cur === null) {
      // Normalmente el efecto de abajo ya lo cargó; esto cubre el fallo de esa
      // carga — y un clic sobre estado desconocido nunca conmuta a ciegas.
      try { cur = !!((await api.getEvent(evt))?.data?.lowLatency); }
      catch (e) { setMessage?.(`Could not read LL state: ${e.message}`); return; }
      setLlByEvt(prev => ({ ...prev, [evt]: cur }));
      setMessage?.(`"${evt}" low latency is ${cur ? 'ON' : 'OFF'} — press again to switch.`);
      return;
    }
    const next = !cur;
    if (!confirmDanger(
      next
        ? `Enable ⚡ low latency for "${evt}"?\n\nThe manifest is rebuilt NOW with availabilityTimeOffset=0.8 — this REQUIRES the chunked chain live (LL ffmpeg preset on the cams + ingest-receiver + nginx fallback). Without it, edge requests become 404s.\n\nThe LL player preset is written to Dash Settings; viewers reposition/reload.`
        : `Disable ⚡ low latency for "${evt}"?\n\nThe manifest is rebuilt NOW back to classic availability and the LL player preset is cleared (custom Dash Settings are left alone). Viewers need a reload.`
    )) return;
    try {
      const r = await api.setLowLatency(evt, next);
      setLlByEvt(prev => ({ ...prev, [evt]: next }));
      const masterInst = Object.values(grouped).flat()
        .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
      // resetLatencyTarget only when ENABLING: the pin was computed under the
      // classic 2000ms floor and would block the 1500ms LL target. Disabling
      // needs no reset — the raised floor re-clamps the pin upward on its own.
      await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true, resetLatencyTarget: next });
      // Refresca la caché del panel Dash Settings (no re-lee del servidor en sesión).
      setDashSettingsContent?.(prev => ({ ...prev, [evt]: r?.data?.dashSettings ?? '' }));
      setMessage?.(`⚡ Low latency ${next ? 'ENABLED' : 'disabled'} for "${evt}" — manifest rebuilt. ${r?.data?.settingsNote || ''} Reload viewers.`);
    } catch (e) {
      setMessage?.(`LL toggle failed: ${e.response?.data?.message || e.message}`);
    }
  };

  // Carga el estado LL de cada evento al pintarlo (el "?" solo dura el primer
  // render). El guard por valor evita re-fetches: `grouped` cambia de identidad
  // en cada poll, pero los eventos ya resueltos se saltan.
  useEffect(() => {
    for (const evt of Object.keys(grouped || {})) {
      if (llByEvt[evt] !== undefined) continue;
      setLlByEvt(prev => (prev[evt] !== undefined ? prev : { ...prev, [evt]: null }));
      api.getEvent(evt)
        .then(r => {
          setLlByEvt(prev => ({ ...prev, [evt]: !!r?.data?.lowLatency }));
          setLiveByEvt(prev => ({ ...prev, [evt]: !!r?.data?.goEpochMs }));
          setMfVersionByEvt(prev => ({ ...prev, [evt]: r?.data?.manifestVersion || null }));
        })
        .catch(() => setLlByEvt(prev => { const n = { ...prev }; delete n[evt]; return n; }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grouped]);

  // === Track order (IR-196): operator-defined cam/audio order in the manifest ===
  // The first video/audio track is the viewer default, so the order is an
  // editorial decision, not an infra one — hence its own panel instead of the
  // instance table (which lists EC2 boxes, not manifest tracks).
  const [trackOrderByEvent, setTrackOrderByEvent] = useState({}); // { [evt]: { videos: [], audios: [], dirty } }
  const [openTrackOrderEvt, setOpenTrackOrderEvt] = useState(null);
  const [trackOrderBusyEvt, setTrackOrderBusyEvt] = useState(null);

  const normCam = (s) => String(s || '').toLowerCase().trim();

  const fetchTrackOrder = async (evt) => {
    try {
      setTrackOrderBusyEvt(evt);
      const { data } = await api.getEvent(evt);
      const frags = (data?.mpdFragments || []).filter(f => f.enabled !== false);
      const dedupe = (role) => {
        const seen = new Set(); const out = [];
        for (const f of frags) {
          if (f.role !== role) continue;
          const key = normCam(f.camName);
          if (!key || seen.has(key)) continue;
          seen.add(key); out.push({ camName: f.camName, instanceId: f.instanceId });
        }
        return out;
      };
      // Mirrors the backend's sortByTrackOrder so the panel shows what the
      // manifest actually emits: saved order first, name sort as fallback.
      const sortWith = (tracks, orderList) => {
        const order = new Map((orderList || []).map((n, i) => [normCam(n), i]));
        return tracks.slice().sort((a, b) => {
          const ia = order.has(normCam(a.camName)) ? order.get(normCam(a.camName)) : Infinity;
          const ib = order.has(normCam(b.camName)) ? order.get(normCam(b.camName)) : Infinity;
          return ia !== ib ? ia - ib : String(a.camName).localeCompare(String(b.camName), undefined, { numeric: true, sensitivity: 'base' });
        });
      };
      // Human labels, resolved the same way the viewer sees them: Event
      // cam/audio names are positional over the CURRENT manifest order, and
      // camLabels (by instanceId) wins for audio like in the manifest build.
      let camLabels = {};
      try { const r = await api.getCamLabels(evt); camLabels = r?.data?.camLabels || {}; } catch {}
      let evInfo = null;
      try {
        const r = await authFetch(`${API_BASE}/event/get`);
        const j = await r.json();
        evInfo = (j?.events || []).find(e => e.title === evt) || null;
      } catch {}
      const vids = sortWith(dedupe('video'), data?.camOrder);
      const auds = sortWith(dedupe('audio'), data?.audioOrder);
      setTrackOrderByEvent(prev => ({
        ...prev,
        [evt]: {
          videos: vids.map((t, i) => ({ camName: t.camName, label: camLabels[t.instanceId] || evInfo?.cam_names?.[i] || '' })),
          audios: auds.map((t, i) => ({ camName: t.camName, label: camLabels[t.instanceId] || evInfo?.audio_names?.[i] || '' })),
          dirty: false,
        },
      }));
      setOpenTrackOrderEvt(evt);
    } catch (e) {
      setMessage?.(`Track order load failed: ${e.message}`);
    } finally {
      setTrackOrderBusyEvt(null);
    }
  };

  const moveTrack = (evt, kind, index, delta) => {
    setTrackOrderByEvent(prev => {
      const cur = prev[evt]; if (!cur) return prev;
      const list = cur[kind].slice();
      const j = index + delta;
      if (j < 0 || j >= list.length) return prev;
      [list[index], list[j]] = [list[j], list[index]];
      return { ...prev, [evt]: { ...cur, [kind]: list, dirty: true } };
    });
  };

  const applyTrackOrder = async (evt) => {
    const cur = trackOrderByEvent[evt]; if (!cur) return;
    if (!window.confirm(
      `Apply the new camera/audio order to "${evt}" and rebuild the manifest?\n\n` +
      `The first camera/audio becomes the viewer default. Event labels and thumbnails are re-ordered to match.`
    )) return;
    try {
      setTrackOrderBusyEvt(evt);
      setMessage?.('Saving track order…');
      await api.setTrackOrder(evt, {
        camOrder: cur.videos.map(v => v.camName),
        audioOrder: cur.audios.map(a => a.camName),
      });
      const masterInst = Object.values(grouped).flat()
        .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
      await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
      if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
      setTrackOrderByEvent(prev => ({ ...prev, [evt]: { ...prev[evt], dirty: false } }));
      setMessage?.(`Track order applied for "${evt}" and manifest rebuilt.`);
    } catch (e) {
      setMessage?.(`Track order failed: ${e.response?.data?.message || e.message}`);
    } finally {
      setTrackOrderBusyEvt(null);
    }
  };
  // `grouped` is rebuilt by a plain reduce in the parent, so it is a new object on
  // every render. Reading it through a ref keeps it out of the poll effect's deps —
  // otherwise each render tore the effect down and restarted the timer, and since
  // renders arrive faster than the interval the tick never fired at all.
  const groupedRef = useRef(grouped);
  useEffect(() => { groupedRef.current = grouped; }, [grouped]);

  // Track in-flight manifest fetches per event to avoid overlapping calls
  const inflightRef = useRef({}); // { [evt]: boolean }

// put this near the top
const initiallyFetched = useRef({}); // { [evt]: true }

// Auto-select RTMP-OK cameras for Start Event. Not just on first appearance:
// an instance usually shows up BEFORE its SRS is reachable, so a select-once
// froze it unselected until a page reload re-ran the "first" pass. Instead,
// promote to selected whenever RTMP comes up — unless the operator touched
// that checkbox manually, in which case their choice always wins.
const manualSelectionRef = useRef({}); // { [instanceId]: true } once hand-toggled
useEffect(() => {
  if (!grouped) return;
  setStartSelection(prev => {
    let changed = false;
    const next = { ...prev };
    for (const list of Object.values(grouped)) {
      for (const inst of list) {
        if ((inst.role || '').toLowerCase() === 'master') continue;
        const id = inst.instanceId;
        if (!(id in next)) {
          next[id] = !!rtmpOk[id];
          changed = true;
        } else if (!manualSelectionRef.current[id] && rtmpOk[id] && !next[id]) {
          next[id] = true;
          changed = true;
        }
      }
    }
    return changed ? next : prev;
  });
}, [grouped, rtmpOk]);

// keep the set clean if events disappear
useEffect(() => {
  const live = new Set(Object.keys(grouped || {}));
  Object.keys(initiallyFetched.current).forEach(k => {
    if (!live.has(k)) delete initiallyFetched.current[k];
  });
}, [grouped]);

// fetch once per event when it first appears
useEffect(() => {
  if (!grouped) return;
  for (const [evt, list] of Object.entries(grouped)) {
    if (initiallyFetched.current[evt]) continue;
    const masterInst = (list || []).find(i => (i.role || '').toLowerCase() === 'master');
    fetchManifestForEvent?.(evt, masterInst);
    initiallyFetched.current[evt] = true;
  }
  // intentionally omit fetchManifestForEvent to avoid re-firing when its identity changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [grouped]);

  // Load fragment enabled states when events appear
  useEffect(() => {
    if (!grouped || !api) return;
    Object.keys(grouped).forEach(async (evt) => {
      try {
        const r = await api.getEvent(evt);
        const frags = r.data?.mpdFragments || [];
        setFragStates(prev => {
          const next = { ...prev };
          for (const f of frags) {
            const key = `${evt}/${(f.camName || '').toLowerCase()}/${f.role}`;
            next[key] = f.enabled !== false;
          }
          return next;
        });
        const mode = r.data?.streamingMode || 'dash';
        setStreamingModeByEvent(prev => ({ ...prev, [evt]: mode }));
        if (mode === 'livekit') {
          setLivekitIngressByEvent(prev => ({ ...prev, [evt]: r.data?.livekitIngress || {} }));
          refreshLivekitParticipants(evt);
        }
      } catch {}
    });
  }, [grouped, api]);

  // ─────────────────────────────────────────────────────────────
  // NEW: Poll manifests via the master proxy at POLL_MS (5s)
  // * Only when the event is RUNNING or its manifest is explicitly opened
  // * Serialized, visibility-aware, and de-duplicated with in-flight guard
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!grouped || !fetchManifestForEvent) return;
    let cancelled = false;
    let timer;

    const scheduleNext = () => {
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    const tick = async () => {
      if (cancelled) return;
      try {
        if (document.visibilityState !== 'visible') return scheduleNext();

        for (const [evt, list] of Object.entries(grouped)) {
          const masterInst = (list || []).find(i => (i.role || '').toLowerCase() === 'master');
          if (!masterInst) continue;

          // Only poll if event is running OR user opened this event's manifest panel
          const camsInEvt = (list || []).filter(i => (i.role || '').toLowerCase() !== 'master');
          const running = camsInEvt.some(c => !!ffmpegStat[c.instanceId]);
          const userRequested = !!showManifest?.[evt];
          if (!running && !userRequested) continue;

          if (inflightRef.current[evt]) continue; // skip if already fetching this event
          inflightRef.current[evt] = true;
          try {
            await fetchManifestForEvent(evt, masterInst);
          } finally {
            inflightRef.current[evt] = false;
          }
        }
      } catch {
        // ignore transient errors
      }
      scheduleNext();
    };

    scheduleNext();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [grouped, ffmpegStat, showManifest, fetchManifestForEvent]);
  // ─────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────
  // Refresh the monitor panel while it is open. Read-only: it observes, it never
  // corrects. Only the open event is polled, and only while the tab is visible,
  // so closing the panel costs nothing.
  // ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!openMonitorEvt) return;
    let cancelled = false;
    let timer;

    const tick = async () => {
      if (cancelled) return;
      const list = groupedRef.current?.[openMonitorEvt];
      if (list && document.visibilityState === 'visible') {
        try { await fetchMonitor(openMonitorEvt, list, { silent: true }); } catch {}
      }
      if (!cancelled) timer = setTimeout(tick, MONITOR_POLL_MS);
    };

    timer = setTimeout(tick, MONITOR_POLL_MS);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openMonitorEvt]);
  // ─────────────────────────────────────────────────────────────

  async function fetchFirstSegTimes(evt) {
    try {
      setFirstSegLoadingEvt(evt);
      const r = await authFetch(`${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/first-segment-times`);
      const j = await r.json();
      if (!r.ok || !j.success) throw new Error(j.message || `HTTP ${r.status}`);
      setFirstSegByEvent(prev => ({
        ...prev,
        [evt]: {
          loadedAt: new Date().toISOString(),
          rows: j.results || []
        }
      }));
      setOpenFirstSegEvt(evt);
    } catch (e) {
      setMessage?.(`First segment fetch error: ${e.message}`);
    } finally {
      setFirstSegLoadingEvt(null);
    }
  }

  async function fetchMonitor(evt, list, { silent = false } = {}) {
    const cams = (list || []).filter(i => (i.role || '').toLowerCase() !== 'master');
    if (!cams.length || !api) return;
    if (!silent) setMonitorLoadingEvt(evt);
    try {
      const rows = await Promise.all(cams.map(async (inst) => {
        const base = { instanceId: inst.instanceId, camName: inst.camName };
        try {
          const d = (await api.getMonitor(inst.instanceId))?.data || {};
          return {
            ...base,
            ok: true,
            // t=0 of this camera's grid. trusted=false means the controller fell
            // back to its own clock (the platform epoch never arrived).
            epochMs:        d.clock?.epochMs ?? null,
            epochTrusted:   !!d.clock?.trusted,
            epochSource:    d.clock?.epochSource ?? null,
            // + = labelling content in the future = viewer sees older frames.
            offsetSegments: d.alignment?.offsetSegments ?? null,
            // Declared signal delay applied as slot shift at spawn (IR-198c).
            signalOffsetSec: d.clock?.signalOffsetSec ?? 0,
            lastSegAgeMs:   d.alignment?.lastSegmentAgeMs ?? null,
            recoveryState:  d.recovery?.state ?? null,
            deaths:         d.recovery?.deaths ?? 0,
            healthyKbps:    d.signal?.healthyKbps ?? null,
            signalPresent:  d.signal?.present ?? null,
            missingSegs:    d.continuity?.missingSegments ?? 0,
            gaps:           d.continuity?.gaps ?? [],
            encoderRunning: !!d.encoder?.running,
          };
        } catch (e) {
          return { ...base, ok: false, error: e.message };
        }
      }));
      setMonitorByEvent(prev => ({
        ...prev,
        [evt]: { loadedAt: new Date().toISOString(), rows },
      }));
      setOpenMonitorEvt(evt);
    } finally {
      if (!silent) setMonitorLoadingEvt(null);
    }
  }

  /**
   * Cross-camera clock check — the panel's most valuable readout.
   *
   * Every camera of an event must anchor its grid on the SAME epoch (the value
   * the backend mints at /ffmpeg/go and stamps as the manifest AST). If two
   * cameras report different clock.epochMs, they are desynchronised and the
   * difference in ms IS the desync — no further measurement needed.
   */
  function monitorAnalysis(rows = []) {
    // STOPPED/IDLE cameras are OUT of the clock comparison: a stopped controller
    // keeps serving its last event's epoch from event-state.json, and after a
    // fresh event start it dragged the reference 25 minutes into the past and
    // painted the running cameras red (false alarm, seen 2026-10-15).
    const usable = rows.filter(r =>
      r.ok && r.epochMs != null && !['STOPPED', 'IDLE'].includes(r.recoveryState));
    if (!usable.length) return { usable, refEpoch: null, epochSpreadMs: null, untrusted: [] };
    const epochs = usable.map(r => r.epochMs);
    const refEpoch = Math.min(...epochs);
    const epochSpreadMs = Math.max(...epochs) - refEpoch;
    const untrusted = usable.filter(r => !r.epochTrusted);
    return { usable, refEpoch, epochSpreadMs, untrusted };
  }

  // visible status computation (uses grace + ffmpegStat)
  function getEncoderStatus(instId) {
    if (isInGrace(instId)) {
      return { label: 'Running', color: COLORS.ok };
    }
    return ffmpegStat[instId]
      ? { label: 'Running', color: COLORS.ok }
      : { label: 'Stopped', color: COLORS.err };
  }

  const terminateAllForEvent = async (evt, list) => {
    const ok = window.confirm(
      `Are you sure you want to TERMINATE ALL instances for the event "${evt}"?\n\n` +
        `This will terminate the MASTER (if any) and ALL camera instances.`
    );
    if (!ok) return;
    try {
      setMessage?.(`Terminating all instances for "${evt}"…`);
      await Promise.all(list.map((inst) => api.terminate(inst.instanceId).catch((e) => e)));
      setMessage?.(`All instances for "${evt}" terminated.`);
      onRefresh?.();
    } catch (e) {
      setMessage?.(`Terminate-all error: ${e.message}`);
    }
  };

  const autoStartEvent = async (evt, list) => {
    startAbortRef.current = false;
    setStartingEvent(true);
    const aborted = () => startAbortRef.current;

    try {
      const masterInst = list.find((i) => (i.role || '').toLowerCase() === 'master');
      if (!masterInst) {
        setMessage?.(`No master for event "${evt}"`);
        setStartingEvent(false);
        return;
      }

      const camsOnly = list
        .filter((i) => (i.role || '').toLowerCase() !== 'master')
        .sort((a, b) =>
          a.camName.localeCompare(b.camName, undefined, { numeric: true, sensitivity: 'base' })
        );

      // Check if any cam is already encoding
      if (camsOnly.some(c => !!ffmpegStat[c.instanceId])) {
        setMessage?.(`Event "${evt}" is already running.`);
        setStartingEvent(false);
        return;
      }

      const eligibleCams = camsOnly.filter(
        (inst) => !!rtmpOk[inst.instanceId] && startSelection[inst.instanceId]
      );
      if (!eligibleCams.length) {
        setMessage?.(`No selected cameras with RTMP OK in "${evt}"`);
        setStartingEvent(false);
        return;
      }

      // Reset stale event session data (AST, fragments, MPD) from previous runs
      try {
        setMessage?.('Resetting event session data…');
        const rs = await api.resetEventSession(evt);
        setLiveByEvt(prev => ({ ...prev, [evt]: false }));
        // New session version → the Event MPD URL for this run (shown once live).
        if (rs?.data?.manifestVersion) {
          setMfVersionByEvt(prev => ({ ...prev, [evt]: rs.data.manifestVersion }));
        }
        // Mirror the reset in local UI state — the DB just cleared this
        // event's contentOffsets, and without this the panel keeps showing
        // the previous session's values until a full page reload.
        setContentOffsets(prev => {
          const next = { ...prev };
          for (const inst of camsOnly) {
            const k = (inst.camName || '').toLowerCase();
            if (k) delete next[k];
          }
          return next;
        });
      } catch (e) {
        setMessage?.(`Warning: session reset failed: ${e.message}`);
      }

      setMessage?.('Applying preselected or DB-default presets to instances…');
      await ensureCommandsUploaded(api, setMessage, evt, eligibleCams);

      // Must land before /ffmpeg/prepare: that's where syncSlate + syncNormalizer
      // read it to size the chain for a camera that may not be publishing yet.
      setMessage?.('Pushing declared ingest profiles to instances…');
      await ensureIngestProfilesUploaded(api, setMessage, evt, eligibleCams);

      // ───────── Template MPD before starting cameras ─────────
      try {
        setMessage?.('Uploading minimal template MPD to master path…');
        if (typeof onUploadTemplateMpd === 'function') {
          await onUploadTemplateMpd(evt, list, masterInst);
        } else {
          await authFetch(
            `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/manifest/template`,
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ masterInstanceId: masterInst.instanceId }),
            }
          );
        }
        setMessage?.('Template MPD published.');
      } catch (e) {
        setMessage?.(`Warning: Could not upload template MPD (continuing): ${e.message}`);
      }
      // ──────────────────────────────────────────────────────────

      await sleep(1000);

      // ═══════════ PHASE 1: Prepare all cameras in parallel ═══════════
      if (aborted()) { setMessage?.('Start aborted.'); setStartingEvent(false); return; }
      // Absorbs variable-time I/O (archive, mount, slate) before spawning FFmpeg.
      // One archiveTs for the whole start: every camera archives the previous
      // session into the same archived_<ts>/ snapshot, so the archived master's
      // "../<cam>/" references keep resolving.
      const archiveTs = new Date().toISOString().replace(/[:.]/g, '-').replace('T', '_').slice(0, 19);
      setMessage?.(`Preparing ${eligibleCams.length} camera(s) (archive, mount, slate)…`);
      const prepareResults = await Promise.allSettled(
        eligibleCams.map(inst =>
          api.prepareFfmpeg(inst.instanceId, evt, inst.camName, archiveTs)
            .then(r => ({ cam: inst.camName, ok: r?.data?.prepared !== false, data: r?.data }))
            .catch(e => ({ cam: inst.camName, ok: false, error: e.message }))
        )
      );
      const prepareFailed = prepareResults
        .map(r => r.value || r.reason)
        .filter(r => !r.ok);
      if (prepareFailed.length) {
        setMessage?.(`Warning: Prepare failed on ${prepareFailed.map(f => f.cam).join(', ')}`);
      }

      await sleep(500);

      // ═══════════ PHASE 2: Go — spawn FFmpeg on all cameras simultaneously ═══════════
      if (aborted()) { setMessage?.('Start aborted.'); setStartingEvent(false); return; }
      setMessage?.(`Spawning FFmpeg on ${eligibleCams.length} camera(s) simultaneously…`);
      // One goRound key per spawn round (same value for every camera, like
      // archiveTs): the backend mints the event-start epoch once per round and
      // injects the SAME eventStartEpochMs into every /ffmpeg/go — the value the
      // controllers anchor their recovery grid on, and the manifest AST. Each
      // round re-mints (the controller re-registers its epoch on every go), so
      // the FINAL round's epoch is the one that stands.
      const goResults = await Promise.allSettled(
        eligibleCams.map(inst =>
          api.goFfmpeg(inst.instanceId, { eventName: evt, camName: inst.camName, goRound: `${archiveTs}:go1` })
            .then(r => ({ cam: inst.camName, ok: r?.data?.started !== false, data: r?.data }))
            .catch(e => ({ cam: inst.camName, ok: false, error: e.message }))
        )
      );
      const goFailed = goResults
        .map(r => r.value || r.reason)
        .filter(r => !r.ok);
      if (goFailed.length) {
        setMessage?.(`Warning: FFmpeg spawn failed on ${goFailed.map(f => f.cam).join(', ')}`);
      }
      // First go round minted the session epoch — the event is RUNNING from here
      // until Stop Event, regardless of what the encoders do.
      if (goFailed.length < eligibleCams.length) {
        setLiveByEvt(prev => ({ ...prev, [evt]: true }));
      }

      // ═══════════ PHASE 3: Poll all cameras for first segment readiness ═══════════
      // Collect createdAtMs per camera to measure first-segment generation time.
      // This is used in Phase 4 to calculate per-camera delays for alignment.
      setMessage?.('Waiting for all cameras to produce first segments…');
      const POLL_INTERVAL = 2000;
      const POLL_TIMEOUT = 60000;
      const pollStart = Date.now();
      const readyCams = new Set();
      const segmentCreatedAt = {}; // instanceId → createdAtMs

      // Grace period covers entire flow (3 restarts + polls ≈ 3 min max)
      // Prevents UI from showing "Stopped" during warm restart cycles
      const graceUntil = Date.now() + 180000;
      setGraceUntilByInstanceId((prev) => {
        const next = { ...prev };
        for (const c of eligibleCams) next[c.instanceId] = graceUntil;
        return next;
      });

      while (readyCams.size < eligibleCams.length && (Date.now() - pollStart) < POLL_TIMEOUT && !aborted()) {
        const notReady = eligibleCams.filter(c => !readyCams.has(c.instanceId));
        await Promise.allSettled(
          notReady.map(async (inst) => {
            try {
              const r = await api.ffmpegReady(inst.instanceId);
              if (r?.data?.ready) {
                readyCams.add(inst.instanceId);
                if (r.data.createdAtMs) segmentCreatedAt[inst.instanceId] = r.data.createdAtMs;
              }
            } catch {}
          })
        );
        setMessage?.(`Segments ready: ${readyCams.size}/${eligibleCams.length} cameras`);
        if (readyCams.size < eligibleCams.length && !aborted()) await sleep(POLL_INTERVAL);
      }
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }

      if (readyCams.size < eligibleCams.length) {
        const missing = eligibleCams.filter(c => !readyCams.has(c.instanceId)).map(c => c.camName);
        setMessage?.(`Warning: Timed out waiting for segments on: ${missing.join(', ')}`);
      }

      // ═══════════ PHASE 4: Warm restart #1 — measure warm timing ═══════════
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }
      setMessage?.('Warm restart #1: stopping all for timing measurement…');
      await Promise.allSettled(
        eligibleCams.map(inst => api.stopFfmpeg(inst.instanceId).catch(() => {}))
      );
      await sleep(3000);

      setMessage?.(`Warm restart #1: re-launching ${eligibleCams.length} camera(s)…`);
      await Promise.allSettled(
        eligibleCams.map(inst =>
          api.goFfmpeg(inst.instanceId, { eventName: evt, camName: inst.camName, goRound: `${archiveTs}:warm1` }).catch(() => {})
        )
      );

      // Poll ready + collect warm createdAtMs
      const warmCreatedAt = {};
      const readyCamsWarm = new Set();
      const pollStartW = Date.now();
      while (readyCamsWarm.size < eligibleCams.length && (Date.now() - pollStartW) < POLL_TIMEOUT && !aborted()) {
        const notReady = eligibleCams.filter(c => !readyCamsWarm.has(c.instanceId));
        await Promise.allSettled(
          notReady.map(async (inst) => {
            try {
              const r = await api.ffmpegReady(inst.instanceId);
              if (r?.data?.ready) {
                readyCamsWarm.add(inst.instanceId);
                if (r.data.createdAtMs) warmCreatedAt[inst.instanceId] = r.data.createdAtMs;
              }
            } catch {}
          })
        );
        setMessage?.(`Warm timing: ${readyCamsWarm.size}/${eligibleCams.length} cameras`);
        if (readyCamsWarm.size < eligibleCams.length && !aborted()) await sleep(POLL_INTERVAL);
      }
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }

      // Calculate delays from warm measurements
      const warmTimes = Object.values(warmCreatedAt);
      const warmMax = warmTimes.length ? Math.max(...warmTimes) : 0;
      const delayByCam = {};
      for (const inst of eligibleCams) {
        const t = warmCreatedAt[inst.instanceId];
        delayByCam[inst.instanceId] = (t && warmMax) ? Math.round(warmMax - t) : 0;
      }
      const delayInfo = eligibleCams.map(c => `${c.camName}:${delayByCam[c.instanceId]}ms`).join(', ');
      setMessage?.(`Warm delays — ${delayInfo}`);

      // ═══════════ PHASE 5: Warm restart #2 — apply delays for final alignment ═══════════
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }
      setMessage?.('Warm restart #2: stopping all for final aligned launch…');
      await Promise.allSettled(
        eligibleCams.map(inst => api.stopFfmpeg(inst.instanceId).catch(() => {}))
      );
      await sleep(3000);

      setMessage?.(`Final launch: ${eligibleCams.length} camera(s) with warm delays…`);
      const regoResults = await Promise.allSettled(
        eligibleCams.map(inst =>
          api.goFfmpeg(inst.instanceId, {
            eventName: evt,
            camName: inst.camName,
            delayMs: delayByCam[inst.instanceId] || 0,
            goRound: `${archiveTs}:final`,
          })
            .then(r => ({ cam: inst.camName, ok: r?.data?.started !== false, data: r?.data }))
            .catch(e => ({ cam: inst.camName, ok: false, error: e.message }))
        )
      );
      const regoFailed = regoResults
        .map(r => r.value || r.reason)
        .filter(r => !r.ok);
      if (regoFailed.length) {
        setMessage?.(`Warning: Final launch failed on ${regoFailed.map(f => f.cam).join(', ')}`);
      }

      // Poll ready — final
      setMessage?.('Waiting for final synchronized segments…');
      const readyCams2 = new Set();
      const pollStart2 = Date.now();
      while (readyCams2.size < eligibleCams.length && (Date.now() - pollStart2) < POLL_TIMEOUT && !aborted()) {
        const notReady2 = eligibleCams.filter(c => !readyCams2.has(c.instanceId));
        await Promise.allSettled(
          notReady2.map(async (inst) => {
            try {
              const r = await api.ffmpegReady(inst.instanceId);
              if (r?.data?.ready) readyCams2.add(inst.instanceId);
            } catch {}
          })
        );
        setMessage?.(`Final segments ready: ${readyCams2.size}/${eligibleCams.length} cameras`);
        if (readyCams2.size < eligibleCams.length && !aborted()) await sleep(POLL_INTERVAL);
      }
      if (aborted()) {
        setMessage?.('Aborting — stopping all cameras…');
        await Promise.allSettled(eligibleCams.map(c => api.stopFfmpeg(c.instanceId).catch(() => {})));
        setGraceUntilByInstanceId(prev => {
          const n = { ...prev }; for (const c of eligibleCams) delete n[c.instanceId]; return n;
        });
        setMessage?.('Start aborted. All cameras stopped.');
        setStartingEvent(false);
        return;
      }

      if (readyCams2.size < eligibleCams.length) {
        const missing2 = eligibleCams.filter(c => !readyCams2.has(c.instanceId)).map(c => c.camName);
        setMessage?.(`Warning: Timed out on final launch for: ${missing2.join(', ')}`);
      }

      // Determine OK cams — those that have segments ready OR are encoding
      const toBool = (v) => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'number') return v !== 0;
        if (typeof v === 'string') {
          const s = v.trim().toLowerCase();
          if (['true', '1', 'yes', 'on', 'running'].includes(s)) return true;
          if (['false', '0', 'no', 'off', 'stopped', ''].includes(s)) return false;
        }
        return false;
      };

      const okCams = [];
      for (const inst of eligibleCams) {
        if (readyCams2.has(inst.instanceId)) {
          okCams.push(inst);
          continue;
        }
        // Fallback: check status for cameras that didn't produce segments in time
        try {
          const base = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/status`)
            .then((r) => r.json())
            .catch(() => ({}));
          const encOn = toBool(base.ffmpegRunning);
          const ingest = !!ingestOk[inst.instanceId];
          if (encOn || ingest) okCams.push(inst);
        } catch {}
      }

      // Parse cam.mpd and save profiles with bandwidth
      const normalizeCodec = (c) => (typeof c === 'string' && c.trim() === 'hvc1') ? 'hvc1.1.6.L180.90' : c;
      const parseCamMpd = (xmlText) => {
        try {
          const pick = (re, def = '') => {
            const m = xmlText.match(re);
            return m ? m[1] : def;
          };
          const codecs = normalizeCodec(
            pick(/<Representation[^>]*\scodecs="([^"]+)"/i, '') ||
            pick(/<AdaptationSet[^>]*\scodecs="([^"]+)"/i, '')
          );
          const width =
            parseInt(
              pick(/<Representation[^>]*\swidth="(\d+)"/i, '') ||
                pick(/<AdaptationSet[^>]*\swidth="(\d+)"/i, ''),
              10
            ) || 0;
          const height =
            parseInt(
              pick(/<Representation[^>]*\sheight="(\d+)"/i, '') ||
                pick(/<AdaptationSet[^>]*\sheight="(\d+)"/i, ''),
              10
            ) || 0;
          const bandwidth = parseInt(pick(/<Representation[^>]*\sbandwidth="(\d+)"/i, ''), 10) || 0;
          return { codecs, width, height, bandwidth };
        } catch {
          return { codecs: '', width: 0, height: 0, bandwidth: 0 };
        }
      };

      async function fetchCamMpdWithRetry(url, attempts = 12, delayMs = 1500) {
        let lastErr = null;
        for (let i = 1; i <= attempts; i++) {
          try {
            const res = await fetch(`${url}?ts=${Date.now()}`, { cache: 'no-store' });
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const xml = await res.text();
            if (xml && xml.includes('<MPD')) return xml;
            throw new Error('Empty/invalid MPD');
          } catch (e) {
            lastErr = e;
            await sleep(delayMs);
          }
        }
        throw lastErr || new Error('cam.mpd not available');
      }

      const camMpdData = [];
      for (const cam of okCams) {
        if (detectRole(cam) !== 'video') continue; // only video cams in event MPD
        const url = `https://d10tner3hocpa9.cloudfront.net/${evt}/${cam.camName}/cam.mpd`;
        setMessage?.(`[cam-mpd] fetching: ${url}`);
        let parsed = null;
        try {
          const xml = await fetchCamMpdWithRetry(url);
          const { codecs, width, height, bandwidth } = parseCamMpd(xml);
          const scale = width && height ? `${width}x${height}` : '';
          setMessage?.(
            `${cam.camName}: codec=${codecs || '(?)'} scale=${scale || '(?)'} bw=${bandwidth ||
              '(?)'} — saving profile…`
          );

          try {
            await authFetch(`${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/cam-profile`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                instanceId: cam.instanceId,
                camName: cam.camName,
                codecs,
                width,
                height,
                scale,
                bandwidth,
              }),
            });
          } catch (e) {
            setMessage?.(`Warning: Could not save profile for ${cam.camName}: ${e.message}`);
          }

          parsed = { codecs, width, height, bandwidth };
        } catch (err) {
          setMessage?.(`${cam.camName} cam.mpd read failed (will include defaults): ${err.message}`);
        }

        camMpdData.push({
          instanceId: cam.instanceId,
          camName: cam.camName,
          publicIp: cam.publicIp,
          role: 'video',
          ...(parsed || {}),
        });
      }

      // Optional backend snapshot (reads cam.mpd server-side as a fallback)
      try {
        await authFetch(
          `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/snapshot-cam-profiles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              cams: okCams
                .filter((c) => detectRole(c) === 'video')
                .map((c) => ({ camName: c.camName, instanceId: c.instanceId })),
            }),
          }
        );
      } catch {
        /* non-fatal */
      }

      // Rebuild/upload with ok cams
      try {
        if (typeof onGenerateAndUploadMpd === 'function') {
          setMessage?.(
            `Rebuilding & uploading MPD (${camMpdData.length} video cam(s) included)…`
          );
          await onGenerateAndUploadMpd(evt, list, masterInst, camMpdData, { resetAst: true, forcePtoZero: true });
          setMessage?.(`MPD rebuilt & uploaded.`);
        }
      } catch (e) {
        setMessage?.(`Warning: Error rebuilding/uploading MPD: ${e.message}`);
      }

      // Start publishTime periodic updater on master
      try {
        await api.publishStart(masterInst.instanceId, evt);
        setMessage?.('publishTime updater started on master.');
      } catch (e) {
        setMessage?.(`Warning: publishTime updater failed: ${e.message}`);
      }

      // ═══════════ PHASE 6: auto-align content offsets from /monitor ═══════════
      // The warm delays equalize spawn timing, but every ffmpeg open still rolls
      // ±1-2s of variance (keyframe phase with gop_cache off, probe time), and
      // whatever it lands on is frozen for the whole session. /monitor's
      // offsetSegments measures that residual exactly (validated 2026-10-13:
      // −3 vs −1 read as exactly 2.0s between the players), so patch it here
      // instead of shipping a start that needs a manual Align click. Runs AFTER
      // the forcePtoZero rebuild on purpose: that build wipes session offsets,
      // so writing them earlier would be undone. Two samples must agree per
      // camera — the reading moves in whole segments and can flicker ±1
      // mid-segment; an unstable camera is left untouched rather than guessed.
      try {
        const videoCams = okCams.filter(c => detectRole(c) === 'video');
        if (videoCams.length >= 2 && !aborted()) {
          setMessage?.('Checking cross-camera alignment…');
          await sleep(4000);
          const sampleOffsets = async () => {
            const out = {};
            await Promise.allSettled(videoCams.map(async c => {
              try {
                const d = (await api.getMonitor(c.instanceId))?.data || {};
                if (d.alignment?.offsetSegments != null) out[(c.camName || '').toLowerCase()] = d.alignment.offsetSegments;
              } catch {}
            }));
            return out;
          };
          const s1 = await sampleOffsets();
          await sleep(3000);
          const s2 = await sampleOffsets();
          const stable = {};
          for (const k of Object.keys(s1)) if (s2[k] === s1[k]) stable[k] = s1[k];
          const keys = Object.keys(stable);
          if (keys.length >= 2) {
            // Reference = highest offset (oldest content): the others are DELAYED
            // to meet it — a camera can never be advanced past its own live edge.
            const maxOff = Math.max(...keys.map(k => stable[k]));
            const changes = keys.map(k => [k, maxOff - stable[k]]).filter(([, v]) => v >= 1);
            if (changes.length) {
              setMessage?.(`Auto-align: ${changes.map(([k, v]) => `${k} +${v}s`).join(', ')} — saving offsets & rebuilding…`);
              for (const [k, v] of changes) {
                const cam = videoCams.find(c => (c.camName || '').toLowerCase() === k);
                setContentOffsets(prev => ({ ...prev, [k]: v }));
                await api.saveContentOffset(evt, cam?.camName || k, v);
              }
              // forcePtoZero: keep the birthtime-based auto PTO off — the monitor
              // offsets above measure the same misalignment, applying both would
              // correct it twice.
              await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
              setMessage?.(`Auto-align applied: ${changes.map(([k, v]) => `${k} +${v}s`).join(', ')}.`);
            } else {
              setMessage?.('Cameras aligned (within ±1s).');
            }
          } else {
            setMessage?.('Auto-align skipped: monitor readings unstable — check the Camera sync panel.');
          }
        }
      } catch (e) {
        setMessage?.(`Auto-align failed (non-fatal): ${e.message}`);
      }

      // End grace
      setGraceUntilByInstanceId((prev) => {
        const next = { ...prev };
        for (const c of eligibleCams) delete next[c.instanceId];
        return next;
      });

      setMessage?.(`Auto-start complete for "${evt}".`);
      onRefresh?.();
      await refreshFragStates(evt);
      try { await fetchManifestForEvent?.(evt, masterInst); } catch {}
    } catch (err) {
      setMessage?.(`Auto-start error: ${err.message}`);
    } finally {
      setStartingEvent(false);
    }
  };

  // Selected instance and current command
  const selectedInstance = useMemo(() => {
    if (!selectedCamInstanceId) return null;
    for (const arr of Object.values(grouped)) {
      const inst = arr.find(i => i.instanceId === selectedCamInstanceId);
      if (inst) return inst;
    }
    return null;
  }, [selectedCamInstanceId, grouped]);
  const currentCmd = selectedInstance
    ? (cmdFromDb[selectedInstance.instanceId] || selectedInstance.command || selectedInstance.ffmpegCommand || '')
    : '';

  // Apply preset
  const handleApplyPreset = async (preset) => {
    if (!selectedInstance || !preset) return;
    setPresetPanelLoading(true);
    try {
      await api.saveEventCmd(selectedInstance.eventName, {
        instanceId: selectedInstance.instanceId,
        command: preset.command,
        role: preset.role || detectRole(selectedInstance)
      });
      await api.updateNodeScript(selectedInstance.instanceId, preset.command);
      setMessage?.(`✅ Success: Preset "${preset.name}" applied to ${selectedInstance.camName}`);
      onRefresh?.();
      setShowPresetPanel(false);
      setSelectedCamInstanceId(null);
    } catch (e) {
      setMessage?.(`Error applying preset: ${e.message}`);
    } finally {
      setPresetPanelLoading(false);
    }
  };

  // --- Start/Stop FFmpeg on a single camera ---
  const startSingleCamEncoding = async (evt, inst) => {
    try {
      // The "Stopped" badge also shows while the controller's OWN recovery is
      // mid-cycle (backoff / waiting for the signal to come back — the encoder
      // is genuinely down for a few seconds then). A manual start there is
      // harmless to the grid (reuseEventEpoch: it lands on its slot, nothing is
      // archived) but it races the supervisor's own relaunch — ask /monitor
      // first and only proceed past an active recovery with confirmation.
      try {
        const m = (await api.getMonitor(inst.instanceId))?.data || {};
        const st = m.recovery?.state;
        if (st === 'RUNNING' && m.encoder?.running) {
          setMessage?.(`${inst.camName}: encoder is already running — the badge was stale, refreshing.`);
          onRefresh?.();
          return;
        }
        if (['BACKOFF', 'WAITING_STABLE', 'WAITING_SIGNAL'].includes(st)) {
          if (!confirmDanger(
            `${inst.camName}: automatic recovery is working on this camera (${String(st).toLowerCase().replace(/_/g, ' ')}) — ` +
            `it relaunches on its own once the signal is back.\n\n` +
            `A manual start is safe (it joins the event grid, nothing is archived) but it races the supervisor's own relaunch.\n\n` +
            `Start it manually anyway?`
          )) return;
        }
      } catch { /* monitor unreachable — proceed, the start may be exactly what fixes it */ }

      setMessage?.(`Starting encoding on ${inst.camName}…`);
      // 1) Ensure FFmpeg command is saved & pushed to node
      await ensureCommandsUploaded(api, setMessage, evt, [inst]);
      // 2) Start FFmpeg directly on the node (sends eventName+camName for remote mount)
      //    reuseEventEpoch: this button restarts ONE camera of an event that may be
      //    live, so the backend must hand it the event's EXISTING grid epoch — a
      //    fresh mint would also move the manifest AST under the other cameras.
      //    With that epoch the camera lands on its grid slot (start_number
      //    injection), nothing is archived and the other cameras are untouched —
      //    this is also the lever for iterating the ffmpeg preset or the Signal
      //    offset mid-event: stop, adjust, start.
      await api.startFfmpeg(inst.instanceId, evt, inst.camName, { reuseEventEpoch: true });
      // 3) Grace period so status shows Running while FFmpeg initializes
      const graceUntil = Date.now() + 15000;
      setGraceUntilByInstanceId(prev => ({ ...prev, [inst.instanceId]: graceUntil }));
      setMessage?.(`Encoding started on ${inst.camName}. Waiting for segments…`);
      onRefresh?.();

      // 4) Wait for segments, then auto-add to MPD
      await sleep(15000);

      // Verify FFmpeg is actually running
      const statusRes = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/status`)
        .then(r => r.json()).catch(() => ({}));
      const toBoolLocal = (v) => {
        if (typeof v === 'boolean') return v;
        if (typeof v === 'string') return ['true','1','yes','running'].includes(v.trim().toLowerCase());
        return false;
      };
      if (!toBoolLocal(statusRes.ffmpegRunning)) {
        setMessage?.(`FFmpeg not running on ${inst.camName} after grace — check logs.`);
        setGraceUntilByInstanceId(prev => { const n = { ...prev }; delete n[inst.instanceId]; return n; });
        return;
      }

      // 4b) Verify grid placement and warm-restart once if the first run landed
      // off. A fresh box's first ffmpeg is cold (CUDA/JIT) and its startup-lag
      // estimate may be default or stale — the injection then misses by a couple
      // of segments (a poisoned 11s estimate landed a join +11 on 2026-10-15).
      // That first run MEASURES the real lag, so one stop+start lands clean.
      // |offset| <= 1 is the system's floor — leave it alone.
      try {
        const m1 = (await api.getMonitor(inst.instanceId))?.data || {};
        const off1 = m1.alignment?.offsetSegments;
        if (off1 != null && Math.abs(off1) >= 2) {
          setMessage?.(`${inst.camName} landed ${off1 > 0 ? '+' : ''}${off1} seg off the grid — warm restart to re-place it…`);
          setGraceUntilByInstanceId(prev => ({ ...prev, [inst.instanceId]: Date.now() + 45000 }));
          await api.stopFfmpeg(inst.instanceId);
          await sleep(3000);
          await api.startFfmpeg(inst.instanceId, evt, inst.camName, { reuseEventEpoch: true });
          await sleep(15000);
          const m2 = (await api.getMonitor(inst.instanceId))?.data || {};
          const off2 = m2.alignment?.offsetSegments;
          setMessage?.(`${inst.camName} re-placed: offset ${off2 == null ? '(no reading yet)' : `${off2 > 0 ? '+' : ''}${off2} seg`}.`);
        }
      } catch { /* non-fatal — the Camera sync panel still shows the truth */ }

      // 5) Snapshot cam profile
      setMessage?.(`Snapshotting profile for ${inst.camName}…`);
      try {
        await authFetch(
          `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/snapshot-cam-profiles`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cams: [{ camName: inst.camName, instanceId: inst.instanceId }] }),
          }
        );
      } catch {}

      // 6) Add MPD fragment + rebuild
      setMessage?.(`Adding ${inst.camName} to MPD…`);
      await api.addMpdFragment(evt, {
        instanceId: inst.instanceId,
        camName: inst.camName,
        role: (inst.role || '').toLowerCase() === 'audio' ? 'audio' : 'video',
      });

      const masterInst = Object.values(grouped).flat().find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
      await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
      await refreshFragStates(evt);

      setGraceUntilByInstanceId(prev => { const n = { ...prev }; delete n[inst.instanceId]; return n; });
      await onRefresh?.();
      if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`${inst.camName} encoding + added to MPD.`);
    } catch (err) {
      setMessage?.(`Start encoding error for ${inst.camName}: ${err.message}`);
    }
  };

  const stopSingleCamEncoding = async (evt, inst) => {
    try {
      setMessage?.(`Stopping encoding on ${inst.camName}…`);
      await api.stopFfmpeg(inst.instanceId);

      // Auto-remove from MPD and rebuild
      setMessage?.(`Removing ${inst.camName} from MPD…`);
      try {
        await api.removeMpdFragment(evt, inst.instanceId);
        const masterInst = Object.values(grouped).flat().find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
        await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
        await refreshFragStates(evt);
        if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
      } catch {}

      setMessage?.(`${inst.camName} stopped & removed from MPD.`);
      onRefresh?.();
    } catch (err) {
      setMessage?.(`Stop encoding error for ${inst.camName}: ${err.message}`);
    }
  };

  // --- MPD CAM TOGGLER LOGIC INTEGRATED ---
  // Helpers to detect cameras present in manifest.mpd
  function parseCamsInMpd(eventKey, manifestXml) {
    const cams = new Set();
    if (typeof manifestXml !== 'string' || !manifestXml.includes('<MPD')) return cams;
    try {
      const re = new RegExp(`/${eventKey}/([^/]+)/cam\\.mpd`, 'ig');
      let m;
      while ((m = re.exec(manifestXml)) !== null) {
        if (m[1]) cams.add(m[1].toLowerCase());
      }
    } catch {}
    try {
      const baseUrlRe = new RegExp(String.raw`<BaseURL>\s*(?:\.\.\/)?([^\s/<>]+)\/\s*<\/BaseURL>`, 'ig');
      let m;
      while ((m = baseUrlRe.exec(manifestXml)) !== null) {
        if (m[1]) cams.add(m[1].toLowerCase());
      }
    } catch {}
    try {
      const reId = /<AdaptationSet[^>]*\sid="([^"]+)"/ig;
      let m;
      while ((m = reId.exec(manifestXml)) !== null) {
        if (m[1]) cams.add(m[1].toLowerCase());
      }
    } catch {}
    return cams;
  }

  const miniIconBtn = (bg) => ({
    width: 28,
    height: 28,
    borderRadius: 6,
    border: 'none',
    cursor: 'pointer',
    fontSize: 18,
    lineHeight: '28px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    background: bg,
  });

  // Handlers to add/remove camera from MPD
  const handleAddCam = async (evt, inst, masterInst, manifestXml) => {
    const cam = inst.camName || inst.instanceId;
    const ok = window.confirm(`You are about to ADD the camera “${cam}” to the event.\nContinue?`);
    if (!ok) return;
    try {
      setMessage?.(`Adding ${cam} to the event...`);
      // 1) snapshot profile
      await authFetch(
        `${API_BASE}/event-wizard/event/${encodeURIComponent(evt)}/snapshot-cam-profiles`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cams: [{ camName: inst.camName, instanceId: inst.instanceId }] }),
        }
      );
      // 2) add fragment
      await api.addMpdFragment(evt, {
        instanceId: inst.instanceId,
        camName: inst.camName,
        role: (inst.role || '').toLowerCase() === 'audio' ? 'audio' : 'video',
      });
      // 3) rebuild + push all 3 MPD variants to master (handled server-side)
      await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
      await refreshFragStates(evt);
      await onRefresh?.();
      await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`Added ${cam} to the event`);
    } catch (e) {
      setMessage?.(`Error adding ${cam}: ${e.message}`);
    }
  };

  const handleRemoveCam = async (evt, inst, masterInst, manifestXml) => {
    const cam = inst.camName || inst.instanceId;
    const ok = window.confirm(`You are about to REMOVE the camera "${cam}" from the event.\nContinue?`);
    if (!ok) return;
    try {
      setMessage?.(`Removing ${cam} from the event...`);
      await api.removeMpdFragment(evt, inst.instanceId);
      // rebuild + push all 3 MPD variants to master (handled server-side)
      await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
      await refreshFragStates(evt);
      await onRefresh?.();
      await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`Removed ${cam} from the event`);
    } catch (e) {
      setMessage?.(`Error removing ${cam}: ${e.message}`);
    }
  };

  const handleToggleFragment = async (evt, camName, role, enabled, masterInst) => {
    try {
      setMessage?.(`${enabled ? 'Enabling' : 'Disabling'} ${role} for ${camName}...`);
      const key = `${evt}/${(camName || '').toLowerCase()}/${role}`;
      setFragStates(prev => ({ ...prev, [key]: enabled }));
      await api.toggleMpdFragment(evt, camName, role, enabled);
      await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
      await refreshFragStates(evt);
      await fetchManifestForEvent?.(evt, masterInst);
      setMessage?.(`${role} for ${camName} ${enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      await refreshFragStates(evt);
      setMessage?.(`Error toggling ${role} for ${camName}: ${e.message}`);
    }
  };

  // --- Server Config helpers ---
  const cfgLoadFrom = async (instanceId) => {
    setCfgLoading(true);
    try {
      if (cfgTab === 'srs') {
        const r = await api.getSrsConfig(instanceId);
        setCfgText(r.data?.config || '');
      } else {
        const r = await api.getControllerSource(instanceId);
        setCfgText(r.data?.source || '');
      }
    } catch (err) {
      const detail = err.response?.data?.message || err.message;
      setMessage?.(`Load failed: ${detail} — is the controller running on this instance?`);
    }
    setCfgLoading(false);
  };

  const cfgApplyAll = async (eventName) => {
    setCfgLoading(true);
    setCfgResults(null);
    setMessage?.(`Saving ${cfgTab === 'srs' ? 'SRS config' : 'controller source'} to all instances...`);
    try {
      const payload = { eventName };
      if (cfgTab === 'srs') payload.srsConfig = cfgText;
      else payload.controllerSource = cfgText;
      const r = await api.applyAll(payload);
      const results = r.data?.results || [];
      setCfgResults(results);
      const ok = results.filter(r => (r.srs === 'ok' || r.controller === 'ok')).length;
      // Controller edits are also published to the S3 dev channel — that's what
      // makes them survive service restarts on dev-channel instances (stable
      // keeps reading its own object and is never affected).
      const s3 = r.data?.s3DevChannel;
      const s3Note = cfgTab === 'controller'
        ? (s3 === 'ok' ? ' Published to S3 dev channel.' : s3 ? ` S3 dev publish FAILED: ${s3}` : '')
        : '';
      setMessage?.(`Saved to ${ok}/${results.length} instances.${s3Note} Reboot instances to apply.`);
    } catch (err) {
      const detail = err.response?.data?.message || err.message;
      setMessage?.(`Save failed: ${detail}`);
    }
    setCfgLoading(false);
  };

  // --- Pre-show / go-live: force every cam's source at once ---
  // Fired in parallel rather than in an await loop, so the skew between cameras is
  // one proxyTick (250ms) plus network jitter instead of the sum of N round-trips.
  // Each instance keeps classifying its camera's health while pinned to slate, so a
  // camera that has been publishing all through the pre-show is already 'ok' and
  // switches on the very next tick — no RECOVERY_MS wait. MIN_DWELL_MS doesn't bite
  // either: it only blocks a return to cam within 15s of the LAST switch, and a
  // pre-show is far longer than that.
  // Manual-return and source-override (logo/pre-show) controls removed with the
  // failover proxy (IR-198): there is no slate feed to pin cameras to anymore.

  // --- Latency target helpers ---
  // `keepDraft` refreshes the panel without touching the input. latencyInfo only
  // changes on a manifest rebuild, so right after saving it still reports the
  // PREVIOUS request — refilling the field from it snapped the value the operator
  // had just typed back to the old one.
  const latencyLoad = async (evtName, { keepDraft = false } = {}) => {
    if (!evtName) return;
    try {
      const r = await api.getEvent(evtName);
      const d = r.data || {};
      setLatencyState({ targetMs: d.latencyTargetMs ?? null, info: d.latencyInfo || null });
      if (keepDraft) return;
      // El valor que REALMENTE posiciona al player es streaming.delay.liveDelay
      // del dashSettings (en LL el <Latency target> del manifest se ignora). Se
      // muestra ESE; si no hay dashSettings, se cae al target del manifest como
      // antes (eventos legacy sin dashSettings).
      let liveDelay = null;
      try {
        const ds = d.dashSettings ? JSON.parse(d.dashSettings) : null;
        const v = ds?.streaming?.delay?.liveDelay;
        if (typeof v === 'number') liveDelay = v;
      } catch {}
      const secs = liveDelay != null
        ? liveDelay
        : (d.latencyInfo?.requestedMs != null
            ? d.latencyInfo.requestedMs / 1000
            : (d.latencyTargetMs != null ? d.latencyTargetMs / 1000 : ''));
      setLatencyDraft(secs === '' ? '' : String(secs));
    } catch (e) {
      setMessage?.(`Could not read latency for "${evtName}": ${e.message}`);
    }
  };

  // Takes effect on the next manifest rebuild, and only lowers the live target on a
  // fresh session — mid-event the backend can raise it but never walk it back down,
  // because that repositions everyone already watching.
  const latencySave = async (evtName) => {
    const secs = latencyDraft === '' ? null : Number(latencyDraft);
    if (secs !== null && (!Number.isFinite(secs) || secs < 0)) {
      setMessage?.('Latency must be a positive number of seconds, or empty to clear.');
      return;
    }
    setLatencySaving(true);
    try {
      const r = await api.setLiveDelay(evtName, secs);
      // In chunked LL the player positions by liveDelay (Dash Settings), not the
      // manifest target — so the backend also merged it into dashSettings. Refresh
      // the panel cache so it shows the new value, and tell the operator to reload
      // the player (liveDelay changes apply on a fresh player load).
      if (r?.data?.dashSettings) {
        setDashSettingsContent?.(prev => ({ ...prev, [evtName]: r.data.dashSettings }));
      }
      setMessage?.(secs === null
        ? `Latency target cleared for "${evtName}" — back to automatic.`
        : `Latency set to ${secs}s for "${evtName}" (player liveDelay). Reload the player to apply.`);
      await latencyLoad(evtName, { keepDraft: true });
    } catch (e) {
      setMessage?.(`Could not set latency: ${e.response?.data?.message || e.message}`);
    }
    setLatencySaving(false);
  };

  // --- Declared ingest helpers ---
  // Loads the saved profiles from Mongo (source of truth) plus the suggestion the
  // backend derives from the last run's MPD fragments, and asks each instance what
  // it currently has and measures. Drafts fall back saved → suggested → empty.
  const ingestLoad = async (evtName, cams) => {
    if (!evtName || !cams?.length) return;
    setIngestResult(null);

    let saved = {}, suggested = {};
    try {
      const r = await api.getIngestProfiles(evtName);
      saved     = r.data?.ingestProfiles || {};
      suggested = r.data?.suggested || {};
    } catch (e) {
      setMessage?.(`Could not read ingest profiles: ${e.message}`);
    }
    setIngestSuggested(suggested);

    const drafts = {};
    for (const cam of cams) {
      const src = saved[cam.camName] || suggested[cam.camName] || {};
      drafts[cam.camName] = {
        width:  src.width  ?? '',
        height: src.height ?? '',
        codec:  src.codec  || 'hevc',
        kbps:   src.kbps   ?? '',
        offsetSeconds: src.offsetSeconds ?? '',
      };
    }
    setIngestDrafts(drafts);

    // Live state per instance: best-effort, an unreachable controller is not fatal.
    const rows = await Promise.all(cams.map(async (cam) => {
      try {
        const r = await api.getIngestConfig(cam.instanceId);
        return [cam.instanceId, r.data];
      } catch (err) {
        return [cam.instanceId, { error: err.response?.data?.message || err.message }];
      }
    }));
    setIngestState(Object.fromEntries(rows));
  };

  // Saves to Mongo and pushes to the instance in one go — same convention as
  // saveEventCmd + updateNodeScript. Mongo is authoritative; the push just makes it
  // take effect at the next Prepare without waiting for a full event start.
  const ingestSaveCam = async (evtName, cam) => {
    const draft = ingestDrafts[cam.camName];
    if (!draft) return;
    setIngestSaving(true);
    setIngestResult(null);
    // '' clears a field, dropping it back to the measured/default path.
    const payload = {
      width:  draft.width  === '' ? '' : Number(draft.width),
      height: draft.height === '' ? '' : Number(draft.height),
      codec:  draft.codec  || '',
      kbps:   draft.kbps   === '' ? '' : Number(draft.kbps),
      // Declared signal latency (persistent, applied as base on every rebuild)
      offsetSeconds: (draft.offsetSeconds === '' || draft.offsetSeconds == null) ? '' : Number(draft.offsetSeconds),
    };
    try {
      await api.saveIngestProfile(evtName, cam.camName, payload);
    } catch (err) {
      const data = err.response?.data;
      setIngestResult([{ name: cam.camName, ok: false, error: data?.errors?.join('; ') || data?.message || err.message }]);
      setIngestSaving(false);
      return;
    }
    let pushed = true, pushErr = null;
    try {
      await api.setIngestConfig(cam.instanceId, payload);
    } catch (err) {
      pushed = false;
      pushErr = err.response?.data?.errors?.join('; ') || err.response?.data?.message || err.message;
    }
    setIngestResult([{
      name: cam.camName, ok: true,
      error: pushed ? null : `saved in DB, not pushed to instance (${pushErr}) — will apply at event start`,
    }]);
    setMessage?.(`Ingest profile saved for ${cam.camName}${pushed ? ' and pushed to its instance' : ' (DB only)'}.`);
    await ingestLoad(evtName, [cam]);
    setIngestSaving(false);
  };

  const ingestApplySuggested = (cams) => {
    setIngestDrafts(prev => {
      const next = { ...prev };
      for (const cam of cams) {
        const s = ingestSuggested[cam.camName];
        if (!s) continue;
        next[cam.camName] = {
          width:  s.width  ?? '',
          height: s.height ?? '',
          codec:  s.codec  || 'hevc',
          kbps:   s.kbps   ?? '',
        };
      }
      return next;
    });
  };

  const setIngestField = (camName, field, value) =>
    setIngestDrafts(prev => ({ ...prev, [camName]: { ...(prev[camName] || {}), [field]: value } }));

  const ingestInputStyle = {
    width: 84, fontSize: 12, padding: '4px 6px', borderRadius: 4,
    color: 'inherit', background: 'rgba(0,0,0,0.25)', border: '1px solid #555',
  };

  const cfgRebootAll = async (eventName) => {
    if (!window.confirm(`Reboot ALL instances for "${eventName}"?\n\nThis will restart SRS, ffmpeg-controller, and any running FFmpeg encoders.`)) return;
    setCfgLoading(true);
    setMessage?.(`Rebooting instances for "${eventName}"...`);
    try {
      const r = await api.rebootInstances(eventName);
      const rebooted = r.data?.rebooted || [];
      setMessage?.(`Rebooted ${rebooted.length} instances: ${rebooted.map(i => i.camName).join(', ')}. They will be back in ~30s.`);
    } catch (err) {
      const detail = err.response?.data?.message || err.message;
      setMessage?.(`Reboot failed: ${detail}`);
    }
    setCfgLoading(false);
  };

  // Reboot ONE server (its EC2 instance) — for a single stuck/broken box without
  // disturbing the rest of the event.
  const cfgRebootOne = async (evt, inst) => {
    if (!window.confirm(`Reboot ONLY ${inst.camName}?\n\nThis restarts that server (SRS + ffmpeg-controller + its encoder). The other cameras keep running. It will be back in ~30s.`)) return;
    setMessage?.(`Rebooting ${inst.camName}…`);
    try {
      const r = await api.rebootInstances(evt, inst.instanceId);
      const n = r.data?.rebooted?.length || 0;
      setMessage?.(n ? `${inst.camName} rebooting — back in ~30s.` : `No instance rebooted (not found?).`);
      onRefresh?.();
    } catch (err) {
      setMessage?.(`Reboot ${inst.camName} failed: ${err.response?.data?.message || err.message}`);
    }
  };

  return (
    <div
      style={{ marginTop: 12 }}
      className={
        document.body.classList.contains('studio-mode')
          ? 'MinimalDashboard studio-minimal'
          : 'MinimalDashboard'
      }
    >
      {/* Modal panel to select FFmpeg preset per camera */}
      {showPresetPanel && selectedInstance && (
        <StudioCamPresetPanel
          instance={selectedInstance}
          presets={presets}
          currentCmd={currentCmd}
          onApplyPreset={handleApplyPreset}
          onClose={() => { setShowPresetPanel(false); setSelectedCamInstanceId(null); }}
          loading={presetPanelLoading}
        />
      )}

      {Object.entries(grouped).map(([evt, list]) => {
        const pendingForEvt = pendingLaunchesByEvent?.[evt] || [];
        const pendingMaster = pendingForEvt.some(
          (p) => (p.role || '').toLowerCase() === 'master'
        );

        const masterInst = list.find((i) => (i.role || '').toLowerCase() === 'master');
        const camsOnly = list.filter((i) => (i.role || '').toLowerCase() !== 'master');

        // Started = session epoch minted and not ended by Stop Event. Encoders
        // are a fallback for legacy events with no goEpochMs: an event with all
        // cameras down is STILL running until the operator stops it.
        const eventStarted = !!liveByEvt[evt] || camsOnly.some(c => !!ffmpegStat[c.instanceId])
          || camsOnly.some(c => isInGrace(c.instanceId));

        const selectedCount = camsOnly.filter(
          (inst) => !!rtmpOk[inst.instanceId] && startSelection[inst.instanceId]
        ).length;
        const eligibleCount = selectedCount;

        const hasAnyPending = pendingForEvt.length > 0;

        return (
          <div
            key={evt}
            style={{ margin: '16px 0', border: '1px solid #ddd', borderRadius: 8, padding: 12 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 8,
                flexWrap: 'wrap',
              }}
            >
              <strong style={{ fontSize: 20 }}>Event: {evt}</strong>

              {masterInst ? (
                <>
                  <Badge
                    ok={eventStarted}
                    on={COLORS.ok}
                    off={COLORS.err}
                    labelOk="Running"
                    labelOff="Stopped"
                  />
                </>
              ) : pendingMaster ? (
                <Pill color={COLORS.warn}>Deploying master…</Pill>
              ) : camsOnly.length > 0 ? (
                // Cams exist but no master: a partially-failed launch (e.g. AZ
                // capacity) leaves the event unstartable with no way to recover
                // from the UI — relaunch just the master.
                <button
                  onClick={async () => {
                    try {
                      setMessage?.(`Launching master for "${evt}"…`);
                      // Same controller channel the operator picked for the event
                      // (persisted in localStorage) so the emergency master relaunch
                      // isn't silently stuck on stable while the cams run dev.
                      const ch = localStorage.getItem('ew.controllerChannel') === 'dev' ? 'dev' : 'stable';
                      const r = await api.launchMaster(evt, undefined, ch);
                      if (!r.data?.success) throw new Error(r.data?.message || 'launch failed');
                      setMessage?.(`Master launching for "${evt}" — it will appear in the list shortly.`);
                      onRefresh?.();
                    } catch (e) {
                      setMessage?.(`Master launch failed: ${e.response?.data?.message || e.message}`);
                    }
                  }}
                  style={{
                    background: COLORS.warn, color: '#fff', border: 'none',
                    borderRadius: 4, padding: '4px 10px', cursor: 'pointer', fontSize: 12,
                  }}
                  title="This event has cameras but no master instance (a previous launch partially failed). Launch only the master."
                >
                  ⚠ No master — Launch master
                </button>
              ) : null}

              {hasAnyPending && (
                <span style={{ marginLeft: 8 }}>
                  <Pill color={COLORS.idle}>Launching…</Pill>
                </span>
              )}

              <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button
                  disabled={!masterInst || eligibleCount === 0 || eventStarted || hasAnyPending || startingEvent}
                  onClick={() => autoStartEvent(evt, list)}
                  title={
                    startingEvent
                      ? 'Startup in progress — use Abort to cancel'
                      : eventStarted
                      ? 'Event already running'
                      : hasAnyPending
                      ? 'Wait for instances to finish launching'
                      : ''
                  }
                  style={{
                    background:
                      !masterInst || eligibleCount === 0 || eventStarted || hasAnyPending || startingEvent
                        ? '#9e9e9e'
                        : '#3f51b5',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor:
                      !masterInst || eligibleCount === 0 || eventStarted || hasAnyPending || startingEvent
                        ? 'not-allowed'
                        : 'pointer',
                  }}
                >
                  ▶ Start Event ({selectedCount} cams)
                </button>

                {startingEvent && (
                  <button
                    onClick={() => { startAbortRef.current = true; }}
                    style={{
                      background: '#f44336',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 4,
                      padding: '6px 10px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                    }}
                  >
                    ✕ Abort Start
                  </button>
                )}

                <button
                  onClick={() => { setMessage?.('Refreshing instances & statuses…'); onRefresh?.(); }}
                  title="Reload the instance list and re-poll every status badge right now (badges also auto-refresh every 2s)"
                  style={{ background: '#607d8b', color: '#fff', border: 'none', borderRadius: 4, padding: '6px 10px', cursor: 'pointer' }}
                >
                  ↻ Refresh
                </button>

                <button
                  disabled={!masterInst}
                  onClick={async () => {
                    try {
                      const ok = window.confirm(
                        `Are you sure you want to stop the event "${evt}"?\n\n` +
                          `This will stop FFmpeg encoding on all cameras.`
                      );
                      if (!ok) return;

                      setMessage?.(`Stopping all encoders for "${evt}"…`);
                      await Promise.all(
                        camsOnly.map(async (inst) => {
                          try {
                            await api.stopFfmpeg(inst.instanceId);
                          } catch {}
                        })
                      );

                      // Stop publishTime updater on master
                      if (masterInst) {
                        try { await api.publishStop(masterInst.instanceId); } catch {}
                      }

                      // End the session: clear the go epoch so the event reads
                      // STOPPED from now on (and survives a page reload). This is
                      // the only "the event ended" switch — encoders dying never
                      // flips it.
                      try { await api.endEventSession(evt); } catch {}
                      setLiveByEvt(prev => ({ ...prev, [evt]: false }));

                      // Generate VOD manifest (static, multi-Period)
                      let vodTs = null;
                      try {
                        setMessage?.('Generating VOD manifest…');
                        const { data } = await api.generateVodMpd(evt, masterInst?.instanceId);
                        if (data?.success) {
                          vodTs = data.timestamp || null;
                          const nPeriods = data.periods?.length || 0;
                          setMessage?.(`VOD manifest generated (${nPeriods} period${nPeriods !== 1 ? 's' : ''}, ${data.duration}).`);
                        }
                      } catch (e) {
                        setMessage?.(`Warning: VOD manifest generation failed: ${e.message}`);
                      }

                      // Archive the just-ended session into archived_<ts>/ NOW, so
                      // it's a clean snapshot ready to copy to S3 for the VOD —
                      // instead of lingering as live cam folders until the next
                      // start. One shared ts for all cameras (+ master). Runs after
                      // the VOD gen, which reads the segments in place. Idempotent
                      // with the start-archive safety net.
                      // Archive server-side (the backend fans out to each
                      // controller's /archive directly — robust, unlike the old
                      // per-cam fan-out from the browser which raced/timed out).
                      let archiveTs = null;
                      try {
                        setMessage?.('Archiving session…');
                        const ra = await api.archiveSession(evt);
                        archiveTs = ra?.data?.archiveTs || null;
                        setMessage?.(archiveTs ? `Session archived into archived_${archiveTs}.` : 'Archive returned no ts.');
                      } catch (e) {
                        setMessage?.(`Warning: archive on stop failed: ${e.response?.data?.message || e.message}`);
                      }

                      // Point the Event at the VOD just generated (now inside
                      // archived_<ts>/) so the VOD is immediately available on the
                      // product page. The next Start repoints sourcesDash/sources
                      // back to the live files (rebuild → updateEventSources).
                      if (vodTs) {
                        try {
                          const r = await api.pointToVod(evt, archiveTs, vodTs);
                          if (r?.data?.success) setMessage?.(`VOD available for "${evt}" (${r.data.sourcesDash}).`);
                        } catch (e) {
                          setMessage?.(`Warning: could not point event to VOD: ${e.message}`);
                        }
                      }

                      onRefresh?.();
                      setMessage?.('Event stopped — VOD available');
                    } catch (err) {
                      setMessage?.(`Stop event error: ${err.message}`);
                    }
                  }}
                  style={{
                    background: !masterInst ? '#9e9e9e' : COLORS.err,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor: !masterInst ? 'not-allowed' : 'pointer',
                  }}
                >
                  ⏹ Stop Event
                </button>

                <button
                  onClick={() => terminateAllForEvent(evt, list)}
                  style={{
                    background: COLORS.err,
                    color: '#fff',
                    border: 'none',
                    borderRadius: 4,
                    padding: '6px 10px',
                    cursor: 'pointer',
                  }}
                  title="Terminate master and all instances in this event"
                >
                  ✖ Terminate All
                </button>
              </div>
            </div>

            {/* Studio launch path — DASH (existing pipeline) vs LiveKit (WHIP,
                parallel, no slate/warmup/drift — see LivekitEventPanel) */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
              <span style={{ fontSize: 12, color: '#888' }}>Path:</span>
              {['dash', 'livekit'].map(m => (
                <button
                  key={m}
                  onClick={() => setEventMode(evt, m)}
                  style={{
                    padding: '4px 12px', borderRadius: 4, fontSize: 12,
                    background: (streamingModeByEvent[evt] || 'dash') === m ? '#9a51e2' : 'transparent',
                    color: (streamingModeByEvent[evt] || 'dash') === m ? '#fff' : 'inherit',
                    border: '1px solid #9a51e2', cursor: 'pointer',
                  }}
                >
                  {m === 'dash' ? 'DASH' : 'LiveKit (WHIP)'}
                </button>
              ))}
            </div>

            {streamingModeByEvent[evt] === 'livekit' ? (
              <LivekitEventPanel
                evt={evt}
                camsOnly={camsOnly}
                presets={presets}
                api={api}
                ingressMap={livekitIngressByEvent[evt]}
                participantsMap={livekitParticipantsByEvent[evt]}
                onRefreshIngress={() => Promise.all([refreshLivekitIngress(evt), refreshLivekitParticipants(evt)])}
                setMessage={setMessage}
              />
            ) : (
            <>

            {/* Server Config collapsible panel (advanced mode) */}
            {advancedMode && (
              <div style={{ marginBottom: 10, border: '1px solid #555', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => setCfgOpen(o => !o)}
                  style={{
                    padding: '8px 12px', background: 'rgba(128,128,128,0.15)', cursor: 'pointer',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8,
                    color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{cfgOpen ? '\u25BC' : '\u25B6'}</span>
                  Server Config
                </div>
                {cfgOpen && (
                  <div style={{ padding: 12 }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      {['srs', 'controller'].map(tab => (
                        <button
                          key={tab}
                          onClick={() => { setCfgTab(tab); setCfgText(''); setCfgResults(null); }}
                          style={{
                            padding: '4px 12px', borderRadius: 4,
                            background: cfgTab === tab ? '#9a51e2' : 'transparent',
                            color: cfgTab === tab ? '#fff' : 'inherit',
                            border: cfgTab === tab ? '1px solid #9a51e2' : '1px solid #888',
                            cursor: 'pointer', fontWeight: cfgTab === tab ? 'bold' : 'normal',
                          }}
                        >
                          {tab === 'srs' ? 'SRS Config' : 'Controller (index.js)'}
                        </button>
                      ))}
                    </div>

                    {/* Load from instance */}
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 13, color: 'inherit' }}>Load from:</span>
                      {camsOnly
                        .slice(0, 8)
                        .map(inst => (
                          <button
                            key={inst.instanceId}
                            disabled={cfgLoading}
                            onClick={() => cfgLoadFrom(inst.instanceId)}
                            style={{
                              padding: '2px 8px', border: '1px solid #888', borderRadius: 4,
                              background: 'transparent', color: 'inherit',
                              cursor: cfgLoading ? 'not-allowed' : 'pointer', fontSize: 12,
                            }}
                          >
                            {inst.camName || inst.instanceId}
                          </button>
                        ))}
                    </div>

                    {/* Editor — collapsed until something is loaded: an empty
                        300px textarea per event ate the whole screen for nothing. */}
                    {!cfgText ? (
                      <div style={{ fontSize: 12, color: '#888', padding: '8px 0' }}>
                        Select a camera above to load its {cfgTab === 'srs' ? 'SRS config' : 'controller source'}.
                      </div>
                    ) : (
                    <>
                    <textarea
                      value={cfgText}
                      onChange={e => setCfgText(e.target.value)}
                      readOnly={cfgTab === 'controller'}
                      style={{
                        width: '100%', minHeight: 300, fontFamily: 'monospace', fontSize: 12,
                        borderRadius: 4, padding: 8, boxSizing: 'border-box',
                        resize: 'vertical', color: 'inherit', background: 'rgba(0,0,0,0.25)',
                        border: '1px solid #555',
                      }}
                      spellCheck={false}
                    />

                    {/* Save + Reboot buttons. No Save for the controller tab: the
                        controller is deployed from S3 (stable/dev channels) and
                        re-downloaded on every service restart, so an edit pushed
                        from here would not survive — the tab is view-only. */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      {cfgTab === 'srs' ? (
                        <button
                          disabled={cfgLoading || !cfgText.trim()}
                          onClick={() => cfgApplyAll(evt)}
                          style={{
                            padding: '6px 14px',
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: cfgLoading || !cfgText.trim() ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {cfgLoading ? 'Saving...' : 'Save to All'}
                        </button>
                      ) : (
                        <span style={{ fontSize: 11, color: '#888' }}>
                          Read-only — the controller deploys from S3 (stable/dev channel), local edits don't survive a restart.
                        </span>
                      )}
                      <button
                        disabled={cfgLoading}
                        onClick={() => cfgRebootAll(evt)}
                        style={{
                          padding: '6px 14px',
                          background: cfgLoading ? '#9e9e9e' : '#f44336',
                          color: '#fff', border: 'none', borderRadius: 4,
                          cursor: cfgLoading ? 'not-allowed' : 'pointer',
                        }}
                      >
                        Reboot Instances
                      </button>
                    </div>
                    </>
                    )}

                    {/* Results */}
                    {cfgResults && (
                      <div style={{ marginTop: 8, fontSize: 13, fontFamily: 'monospace' }}>
                        {cfgResults.map((r, i) => {
                          const ok = r.srs === 'ok' || r.controller === 'ok';
                          return (
                            <div key={i} style={{ padding: '2px 0', color: ok ? '#4caf50' : '#f44336' }}>
                              {ok ? '\u2713' : '\u2717'}{' '}
                              {r.camName || r.instanceId}
                              {r.srs && ` \u2014 SRS: ${r.srs}`}
                              {r.controller && ` \u2014 Controller: ${r.controller}`}
                              {r.error && ` \u2014 ${r.error}`}
                            </div>
                          );
                        })}
                      </div>
                    )}


                    {/* Latency target */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #555' }}>
                      <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'inherit' }}>Latency</h4>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 8, lineHeight: 1.4 }}>
                        How far behind the live edge the player sits. Must stay above the largest
                        camera offset, or that camera is positioned past its own edge and its
                        retries stall the others. The backend raises it on its own when the offsets
                        need it, and only lowers it on a fresh session — never under a live audience.
                        Leave empty for automatic.
                      </div>

                      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          disabled={latencySaving}
                          onClick={() => latencyLoad(evt)}
                          style={{
                            padding: '6px 14px', color: '#fff', border: 'none', borderRadius: 4,
                            cursor: latencySaving ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Load
                        </button>
                        <label style={{ fontSize: 12 }}>
                          Target{' '}
                          <input
                            type="number" min="0" step="0.1" value={latencyDraft}
                            onChange={e => setLatencyDraft(e.target.value)}
                            placeholder="auto" style={ingestInputStyle}
                          />
                          {' '}s
                        </label>
                        <button
                          disabled={latencySaving}
                          onClick={() => latencySave(evt)}
                          style={{
                            padding: '6px 14px', background: latencySaving ? '#9e9e9e' : '#9a51e2',
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: latencySaving ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {latencySaving ? 'Saving…' : 'Set'}
                        </button>
                        {/* "Set" alone can only ever raise the target: the pin blocks
                            lowering so a live audience is never repositioned by surprise.
                            Lowering therefore needs an explicit unpin, which is this. */}
                        <button
                          disabled={latencySaving}
                          onClick={async () => {
                            const masterInst = Object.values(grouped).flat()
                              .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
                            if (!confirmDanger(
                              `Unpin the latency target for "${evt}" and rebuild the manifest?\n\n` +
                              `It will be recomputed from the current offsets, so it can DROP — ` +
                              `every connected viewer repositions. Safe between takes, disruptive on air.`
                            )) return;
                            setLatencySaving(true);
                            try {
                              await api.rebuildMpd(evt, [], masterInst?.instanceId, { resetLatencyTarget: true, forcePtoZero: true });
                              setMessage?.(`Latency target unpinned and manifest rebuilt for "${evt}".`);
                              await latencyLoad(evt);
                            } catch (e) {
                              setMessage?.(`Unpin failed: ${e.response?.data?.message || e.message}`);
                            } finally {
                              setLatencySaving(false);
                            }
                          }}
                          title="Clear the pinned target and rebuild — the only way to lower it without restarting the session"
                          style={{
                            padding: '6px 14px', background: latencySaving ? '#9e9e9e' : COLORS.warn,
                            color: '#fff', border: 'none', borderRadius: 4,
                            cursor: latencySaving ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Unpin &amp; rebuild
                        </button>
                      </div>

                      {latencyState && (
                        <div style={{ marginTop: 8, fontSize: 12, fontFamily: 'monospace', opacity: 0.8 }}>
                          {latencyState.targetMs == null ? (
                            <div>Not computed yet — start the event once, then Load.</div>
                          ) : (
                            <>
                              <div>
                                in manifest: {(latencyState.targetMs / 1000).toFixed(1)}s
                                {/* Two different reasons the target can exceed the request, and
                                    blaming the offsets for both sends you hunting for an offset
                                    that isn't there. If the safe floor already covers the request,
                                    the excess is a pin carried from earlier in the session — and a
                                    pin only ever rises, so lowering liveDelay now changes nothing
                                    until the session is reset. */}
                                {latencyState.info?.requestedMs != null &&
                                  latencyState.info.requestedMs !== latencyState.targetMs &&
                                  (latencyState.info.recommendedMs > latencyState.info.requestedMs
                                    ? ` (you asked for ${(latencyState.info.requestedMs / 1000).toFixed(1)}s — raised to cover the offsets)`
                                    : ` (you asked for ${(latencyState.info.requestedMs / 1000).toFixed(1)}s — held by the pinned value; reset the session to lower it)`)}
                              </div>
                              {latencyState.info && (
                                <>
                                  <div>
                                    worst offset: {(latencyState.info.worstOffsetMs / 1000).toFixed(2)}s
                                    {latencyState.info.worstOffsetCam ? ` on ${latencyState.info.worstOffsetCam}` : ''}
                                  </div>
                                  <div style={{
                                    color: latencyState.info.recommendedMs < latencyState.targetMs ? '#4caf50' : 'inherit'
                                  }}>
                                    safe floor: {(latencyState.info.recommendedMs / 1000).toFixed(1)}s
                                    {latencyState.info.recommendedMs < latencyState.targetMs &&
                                      ` — you could cut ~${((latencyState.targetMs - latencyState.info.recommendedMs) / 1000).toFixed(1)}s`}
                                  </div>
                                  {/* The target is pinned per session and only ever rises, so a value
                                      computed before this session started is carried over — say when,
                                      or a stale pin is indistinguishable from a fresh one. */}
                                  {latencyState.info.computedAt && (() => {
                                    const when = new Date(latencyState.info.computedAt);
                                    const stale = (Date.now() - when.getTime()) > 6 * 60 * 60 * 1000;
                                    return (
                                      <div style={{ color: stale ? COLORS.warn : 'inherit' }}>
                                        pinned at: {when.toLocaleString()}
                                        {stale && ' — older than this session? reset to recompute'}
                                      </div>
                                    );
                                  })()}
                                </>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Declared ingest profiles (per camera) */}
                    <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid #555' }}>
                      <h4 style={{ margin: '0 0 4px', fontSize: 14, color: 'inherit' }}>Ingest (declared)</h4>
                      <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 8, lineHeight: 1.4 }}>
                        Each rig's real parameters. Used to size slate + normalizer <b>before</b> the
                        camera publishes, which is what lets an event start on the logo alone. A live
                        measurement always wins over these. Stored per camera in the DB and pushed to
                        the instance at event start, so a relaunched instance re-seeds itself.
                        Applied at the next <b>Prepare</b> \u2014 saving never restarts anything mid-event.
                      </div>

                      <div style={{ display: 'flex', gap: 8, marginBottom: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                        <button
                          disabled={ingestSaving || !camsOnly.length}
                          onClick={() => ingestLoad(evt, camsOnly)}
                          style={{
                            padding: '6px 14px', color: '#fff', border: 'none', borderRadius: 4,
                            cursor: ingestSaving || !camsOnly.length ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Load
                        </button>
                        <button
                          disabled={ingestSaving || !Object.keys(ingestSuggested).length}
                          onClick={() => ingestApplySuggested(camsOnly)}
                          title="Fill the fields from what the last run's manifest reported for each cam"
                          style={{
                            padding: '6px 14px', background: 'transparent', color: 'inherit',
                            border: '1px solid #777', borderRadius: 4,
                            cursor: ingestSaving || !Object.keys(ingestSuggested).length ? 'not-allowed' : 'pointer',
                          }}
                        >
                          Use last run's values
                        </button>
                      </div>

                      {!camsOnly.length && (
                        <div style={{ fontSize: 12, opacity: 0.6 }}>No cameras in this event.</div>
                      )}

                      {camsOnly.map((cam) => {
                        const draft = ingestDrafts[cam.camName] || { width: '', height: '', codec: 'hevc', kbps: '' };
                        const sug   = ingestSuggested[cam.camName];
                        const live  = ingestState[cam.instanceId];
                        const eff   = live?.effective || {};
                        return (
                          <div key={cam.instanceId} style={{ padding: '8px 0', borderTop: '1px solid #444' }}>
                            <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 4 }}>
                              {cam.camName || cam.instanceId}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                              <label style={{ fontSize: 12 }}>
                                W{' '}
                                <input
                                  type="number" min="1" value={draft.width}
                                  onChange={e => setIngestField(cam.camName, 'width', e.target.value)}
                                  placeholder="7680" style={ingestInputStyle}
                                />
                              </label>
                              <label style={{ fontSize: 12 }}>
                                H{' '}
                                <input
                                  type="number" min="1" value={draft.height}
                                  onChange={e => setIngestField(cam.camName, 'height', e.target.value)}
                                  placeholder="3840" style={ingestInputStyle}
                                />
                              </label>
                              <label style={{ fontSize: 12 }}>
                                Codec{' '}
                                <select
                                  value={draft.codec}
                                  onChange={e => setIngestField(cam.camName, 'codec', e.target.value)}
                                  style={ingestInputStyle}
                                >
                                  <option value="hevc">hevc</option>
                                  <option value="h264">h264</option>
                                </select>
                              </label>
                              <label style={{ fontSize: 12 }}>
                                Kbps{' '}
                                <input
                                  type="number" min="1" value={draft.kbps}
                                  onChange={e => setIngestField(cam.camName, 'kbps', e.target.value)}
                                  placeholder="35000" style={ingestInputStyle}
                                />
                              </label>
                              <label
                                style={{ fontSize: 12 }}
                                title="Inherent signal latency of this rig, in whole seconds. Delays this camera's grid slots at encode time so it lines up with slower transports (e.g. an SRT feed arriving 3s ahead of an RTMP one gets 3). Delay only — to compensate a LATE feed, delay the other cameras. Takes effect on the next start of this camera. Persists across sessions, unlike the drift-panel offsets."
                              >
                                Signal offset (s){' '}
                                <input
                                  type="number" step="1" min="0" max="30" value={draft.offsetSeconds ?? ''}
                                  onChange={e => setIngestField(cam.camName, 'offsetSeconds', e.target.value)}
                                  placeholder="0" style={ingestInputStyle}
                                />
                              </label>
                              <button
                                disabled={ingestSaving}
                                onClick={() => ingestSaveCam(evt, cam)}
                                style={{
                                  padding: '5px 14px', background: ingestSaving ? '#9e9e9e' : '#9a51e2',
                                  color: '#fff', border: 'none', borderRadius: 4, fontSize: 12,
                                  cursor: ingestSaving ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Save
                              </button>
                            </div>

                            <div style={{ fontSize: 11, fontFamily: 'monospace', opacity: 0.7, marginTop: 4, paddingLeft: 2 }}>
                              {sug && (
                                <div>
                                  last run: {sug.width}x{sug.height} {sug.codec || '?'} {sug.kbps ? `${sug.kbps}kbps` : ''}
                                </div>
                              )}
                              {live?.error && <div style={{ color: '#f44336' }}>instance: {live.error}</div>}
                              {live && !live.error && (
                                <>
                                  <div>
                                    measured: {live.measured
                                      ? `${live.measured.w}x${live.measured.h} ${live.measured.codec} ${live.measured.kbps}kbps`
                                      : 'not publishing'}
                                  </div>
                                  <div>
                                    effective: {eff.w || '?'}x{eff.h || '?'} {eff.codec || '?'} {eff.kbps || 0}kbps
                                    {eff.from && ` (res ${eff.from.resolution}, codec ${eff.from.codec}, kbps ${eff.from.kbps})`}
                                  </div>
                                </>
                              )}
                              {live?.mismatch && (
                                <div style={{ color: live.mismatch.breaking ? '#f44336' : '#ff9800' }}>
                                  {live.mismatch.breaking ? '\u26a0 BREAKING mismatch' : '\u26a0 mismatch'}:{' '}
                                  {live.mismatch.fields.map(f =>
                                    `${f.field} declared=${f.declared} measured=${f.measured}${f.driftPct ? ` (${f.driftPct}%)` : ''}`
                                  ).join(', ')}
                                  {' '}&mdash; nothing was resynced; fix above and Prepare again.
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {ingestResult && (
                        <div style={{ marginTop: 6, fontSize: 12, fontFamily: 'monospace' }}>
                          {ingestResult.map((r, i) => (
                            <div key={i} style={{ color: r.ok ? '#4caf50' : '#f44336' }}>
                              {r.ok ? '\u2713' : '\u2717'} {r.name} {r.error ? `\u2014 ${r.error}` : ''}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}


            {/* Manifest collapsible panel (advanced mode) */}
            {advancedMode && (
              <div style={{ marginBottom: 10, border: '1px solid #555', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => {
                    const opening = !mpdOpen;
                    setMpdOpen(opening);
                    if (opening && masterInst) {
                      fetchManifestForEvent?.(evt, masterInst);
                    }
                  }}
                  style={{
                    padding: '8px 12px', background: 'rgba(128,128,128,0.15)', cursor: 'pointer',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8, color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{mpdOpen ? '\u25BC' : '\u25B6'}</span>
                  Manifest
                  <a
                    href={`https://d10tner3hocpa9.cloudfront.net/${evt}/master/manifest.mpd`}
                    target="_blank" rel="noopener noreferrer"
                    onClick={e => e.stopPropagation()}
                    style={{ color: '#9a51e2', fontSize: 11, fontWeight: 'normal', marginLeft: 'auto' }}
                  >CloudFront MPD</a>
                </div>
                {mpdOpen && (
                  <div style={{ padding: 12 }}>
                    {/* Tabs */}
                    <div style={{ display: 'flex', gap: 4, marginBottom: 8 }}>
                      {[['main', 'manifest.mpd'], ['8k', 'manifest_8K.mpd'], ['full', 'manifest_full.mpd']].map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setMpdTab(key)}
                          style={{
                            padding: '4px 12px', borderRadius: 4,
                            background: mpdTab === key ? '#9a51e2' : 'transparent',
                            color: mpdTab === key ? '#fff' : 'inherit',
                            border: mpdTab === key ? '1px solid #9a51e2' : '1px solid #888',
                            cursor: 'pointer', fontWeight: mpdTab === key ? 'bold' : 'normal', fontSize: 12,
                          }}
                        >{label}</button>
                      ))}
                    </div>

                    {manifestMeta[evt]?.source && (
                      <div style={{ fontSize: 10, color: '#888', marginBottom: 4 }}>Source: {manifestMeta[evt].source}</div>
                    )}

                    {/* Editor textarea */}
                    <textarea
                      value={
                        mpdTab === 'main' ? (mpdEdit[evt] ?? manifestText[evt] ?? '')
                        : mpdTab === '8k' ? (mpdEdit8K[evt] ?? manifestText8K[evt] ?? '')
                        : (mpdEditFull[evt] ?? manifestTextFull[evt] ?? '')
                      }
                      onChange={e => {
                        const val = e.target.value;
                        if (mpdTab === 'main') setMpdEdit(v => ({ ...v, [evt]: val }));
                        else if (mpdTab === '8k') setMpdEdit8K(v => ({ ...v, [evt]: val }));
                        else setMpdEditFull(v => ({ ...v, [evt]: val }));
                      }}
                      style={{
                        width: '100%', minHeight: 250, fontFamily: 'monospace', fontSize: 11,
                        borderRadius: 4, padding: 8, boxSizing: 'border-box',
                        resize: 'vertical', color: '#ccc', background: '#181828',
                        border: '1px solid #444',
                      }}
                      spellCheck={false}
                    />

                    {/* Action buttons */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <button
                        disabled={mpdSaving}
                        onClick={async () => {
                          try {
                            setMpdSaving(true);
                            const main = mpdEdit[evt] ?? manifestText[evt] ?? '';
                            const m8k = mpdEdit8K[evt] ?? manifestText8K[evt] ?? '';
                            const mFull = mpdEditFull[evt] ?? manifestTextFull[evt] ?? '';
                            if (typeof saveAndPushMpd === 'function') {
                              await saveAndPushMpd(evt, main, masterInst, m8k || undefined, mFull || undefined);
                            }
                            setManifestText(v => ({ ...v, [evt]: main }));
                            if (m8k) setManifestText8K(v => ({ ...v, [evt]: m8k }));
                            if (mFull) setManifestTextFull(v => ({ ...v, [evt]: mFull }));
                            // Clear edit buffers
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMessage?.('Manifests saved & pushed to master.');
                          } catch (err) {
                            setMessage?.(`Save manifest error: ${err.message}`);
                          } finally {
                            setMpdSaving(false);
                          }
                        }}
                        style={{ padding: '6px 14px', background: mpdSaving ? '#555' : '#4caf50', color: '#fff', border: 'none', borderRadius: 4, cursor: mpdSaving ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >{mpdSaving ? 'Saving…' : 'Save All Variants'}</button>
                      <button
                        onClick={async () => {
                          if (masterInst) {
                            await fetchManifestForEvent?.(evt, masterInst);
                            // Clear edit buffers to show fresh data
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                          }
                        }}
                        style={{ padding: '6px 14px', background: '#555', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12 }}
                      >Reload</button>
                      <button
                        disabled={!masterInst || mpdSaving}
                        onClick={async () => {
                          try {
                            setMessage?.('Rebuilding MPD…');
                            const videoCams = camsOnly.filter(c => detectRoleFromInst(c) === 'video' && ffmpegStat[c.instanceId]);
                            const camMpdData = [];
                            for (const cam of videoCams) {
                              try {
                                const url = `https://d10tner3hocpa9.cloudfront.net/${evt}/${cam.camName}/cam.mpd`;
                                const res = await fetch(url, { cache: 'no-store' });
                                if (res.ok) {
                                  const xml = await res.text();
                                  if (xml && xml.includes('<MPD')) camMpdData.push({ cam, xml });
                                }
                              } catch {}
                            }
                            if (typeof onGenerateAndUploadMpd === 'function') {
                              await onGenerateAndUploadMpd(evt, list, masterInst, camMpdData, { resetAst: false });
                              setMessage?.(`MPD rebuilt & uploaded (${camMpdData.length} cam${camMpdData.length !== 1 ? 's' : ''}).`);
                            }
                            if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
                            // Clear edit buffers
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                          } catch (err) {
                            setMessage?.(`Rebuild MPD error: ${err.message}`);
                          }
                        }}
                        style={{ padding: '6px 14px', background: !masterInst ? '#555' : '#9333ea', color: '#fff', border: 'none', borderRadius: 4, cursor: !masterInst ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >Rebuild MPD</button>
                      <button
                        disabled={!masterInst}
                        onClick={async () => {
                          try {
                            setMessage?.('Rebuilding MPD with PTO=0…');
                            const videoCams = camsOnly.filter(c => detectRoleFromInst(c) === 'video' && ffmpegStat[c.instanceId]);
                            const camMpdData = [];
                            for (const cam of videoCams) {
                              try {
                                const url = `https://d10tner3hocpa9.cloudfront.net/${evt}/${cam.camName}/cam.mpd`;
                                const res = await fetch(url, { cache: 'no-store' });
                                if (res.ok) {
                                  const xml = await res.text();
                                  if (xml && xml.includes('<MPD')) camMpdData.push({ cam, xml });
                                }
                              } catch {}
                            }
                            if (typeof onGenerateAndUploadMpd === 'function') {
                              await onGenerateAndUploadMpd(evt, list, masterInst, camMpdData, { resetAst: false, forcePtoZero: true });
                              setMessage?.(`MPD rebuilt with PTO=0 (${camMpdData.length} cam${camMpdData.length !== 1 ? 's' : ''}).`);
                            }
                            if (masterInst) await fetchManifestForEvent?.(evt, masterInst);
                            setMpdEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEdit8K(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMpdEditFull(v => { const n = { ...v }; delete n[evt]; return n; });
                          } catch (err) {
                            setMessage?.(`Rebuild PTO=0 error: ${err.message}`);
                          }
                        }}
                        style={{ padding: '6px 14px', background: !masterInst ? '#555' : '#0891b2', color: '#fff', border: 'none', borderRadius: 4, cursor: !masterInst ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >Rebuild PTO=0</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* DASH Settings collapsible panel (advanced mode) */}
            {advancedMode && (
              <div style={{ marginBottom: 10, border: '1px solid #555', borderRadius: 8, overflow: 'hidden' }}>
                <div
                  onClick={() => setDashSettingsOpen(!dashSettingsOpen)}
                  style={{
                    padding: '8px 12px', background: 'rgba(128,128,128,0.15)', cursor: 'pointer',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 8, color: 'inherit',
                  }}
                >
                  <span style={{ fontSize: 12 }}>{dashSettingsOpen ? '\u25BC' : '\u25B6'}</span>
                  DASH Settings
                </div>
                {dashSettingsOpen && (
                  <div style={{ padding: 12 }}>
                    <div style={{ fontSize: 10, color: '#888', marginBottom: 8 }}>
                      JSON settings object to merge with default getDashMediaPlayerLiveSettings() when initialising dash.js for this event.
                    </div>
                    
                    <textarea
                      value={dashSettingsEdit[evt] ?? (dashSettingsContent?.[evt] || JSON.stringify(getDashMediaPlayerLiveSettings(), null, 2))}
                      onChange={e => setDashSettingsEdit(v => ({ ...v, [evt]: e.target.value }))}
                      style={{
                        width: '100%', minHeight: 150, fontFamily: 'monospace', fontSize: 11,
                        borderRadius: 4, padding: 8, boxSizing: 'border-box',
                        resize: 'vertical', color: '#ccc', background: '#181828',
                        border: '1px solid #444',
                      }}
                      spellCheck={false}
                    />
                    
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                      <button
                        disabled={dashSettingsSaving}
                        onClick={async () => {
                          try {
                            setDashSettingsSaving(true);
                            const val = dashSettingsEdit[evt] ?? dashSettingsContent?.[evt] ?? '';
                            if (val.trim() !== '') {
                              // Verify it's valid JSON
                              JSON.parse(val);
                            }
                            if (typeof saveDashSettings === 'function') {
                              await saveDashSettings(evt, val);
                            }
                            if (typeof setDashSettingsContent === 'function') {
                              setDashSettingsContent(v => ({ ...v, [evt]: val }));
                            }
                            setDashSettingsEdit(v => { const n = { ...v }; delete n[evt]; return n; });
                            setMessage?.('DASH Settings saved.');
                          } catch (err) {
                            setMessage?.(`Save DASH Settings error: ${err.message}`);
                          } finally {
                            setDashSettingsSaving(false);
                          }
                        }}
                        style={{ padding: '6px 14px', background: dashSettingsSaving ? '#555' : '#4caf50', color: '#fff', border: 'none', borderRadius: 4, cursor: dashSettingsSaving ? 'not-allowed' : 'pointer', fontSize: 12 }}
                      >{dashSettingsSaving ? 'Saving…' : 'Save Settings'}</button>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Cams table */}
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto', fontSize: 12 }}>
                <thead>
                  <tr>
                    {!eventStarted && (
                      <th style={{ borderBottom: '1px solid #eee', padding: '4px', textAlign: 'center', width: 28 }}>
                        <input
                          type="checkbox"
                          title="Select/deselect all"
                          checked={camsOnly.length > 0 && camsOnly.every(c => startSelection[c.instanceId])}
                          onChange={e => {
                            const val = e.target.checked;
                            setStartSelection(prev => {
                              const next = { ...prev };
                              camsOnly.forEach(c => { next[c.instanceId] = val; });
                              return next;
                            });
                          }}
                        />
                      </th>
                    )}
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Cam</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Ingest URL</th>
                    {/* TCP check on port 1935: the SRS server is up and listening.
                        Says nothing about the camera — that's the Ingest column. */}
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}
                        title="SRS server reachable on port 1935 (instance up). Camera publishing is the Ingest column.">SRS</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Ingest</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Encoder</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Speed</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>Monitor</th>
                    <th style={{ borderBottom: '1px solid #eee', padding: '4px 6px', textAlign: 'center', whiteSpace: 'nowrap' }}>MPD</th>
                  </tr>
                </thead>
                <tbody>
                  {camsOnly.map((inst) => {
                    const encoderStatus = getEncoderStatus(inst.instanceId);
                    // Detect if camera is present in the manifest
                    const camsInMpdSet = parseCamsInMpd(evt, manifestText[evt] || '');
                    const inMpd = camsInMpdSet.has((inst.camName || '').toLowerCase());
                    const isExpanded = advancedMode && expandedRowId === inst.instanceId;
                    const totalCols = eventStarted ? 8 : 9;
                    return (
                      <React.Fragment key={inst.instanceId}>
                      <tr
                        style={{ cursor: 'pointer', fontSize: '1em', background: isExpanded ? 'rgba(154,81,226,0.08)' : undefined }}
                        onClick={e => {
                          if (e.target && e.target.dataset && e.target.dataset.mpdbtn) return;
                          if (advancedMode) {
                            const opening = expandedRowId !== inst.instanceId;
                            setExpandedRowId(opening ? inst.instanceId : null);
                            if (opening) {
                              setExpandedTab('preset');
                              setEditCmd(cmdFromDb[inst.instanceId] || '');
                              setLogText('');
                              // detect preset by normalized command match
                              const role = detectRoleFromInst(inst);
                              const normCmd = (cmdFromDb[inst.instanceId] || '').replace(/\s+/g, ' ').trim();
                              const matched = normCmd
                                ? presets.find(p => (!p.role || p.role === role) && (p.command || '').replace(/\s+/g, ' ').trim() === normCmd)
                                : null;
                              setAdvPresetId(matched?._id || '');
                              setAdvPresetName(matched?.name || '');
                              setAdvPresetDesc(matched?.description || '');
                            }
                          } else {
                            setSelectedCamInstanceId(inst.instanceId);
                            setShowPresetPanel(true);
                          }
                        }}
                      >
                        {!eventStarted && (
                          <td
                            style={{
                              borderBottom: '1px solid #f5f5f5',
                              padding: '4px',
                              textAlign: 'center',
                              width: 28,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={!!startSelection[inst.instanceId]}
                              data-mpdbtn
                              onClick={e => e.stopPropagation()}
                              onChange={e => {
                                manualSelectionRef.current[inst.instanceId] = true;
                                setStartSelection(prev => ({
                                  ...prev,
                                  [inst.instanceId]: e.target.checked,
                                }));
                              }}
                            />
                          </td>
                        )}
                        <td
                          style={{
                            borderBottom: '1px solid #f5f5f5',
                            padding: '4px 6px',
                            textAlign: 'center',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div>{inst.camName}</div>
                          <input
                            type="text"
                            placeholder="Label..."
                            value={camLabels[inst.instanceId] || ''}
                            onChange={e => updateCamLabel(evt, inst.instanceId, e.target.value)}
                            onClick={e => e.stopPropagation()}
                            style={{
                              width: '100%',
                              border: 'none',
                              background: 'transparent',
                              color: '#ccc',
                              fontSize: 10,
                              textAlign: 'center',
                              outline: 'none',
                              marginTop: 2,
                              padding: '1px 0',
                            }}
                          />
                          <div style={{ fontSize: 10, color: '#999' }}>{inst.instanceType || 'g6.2xlarge'}</div>
                        </td>
                        <td
                          style={{
                            borderBottom: '1px solid #f5f5f5',
                            padding: '4px 6px',
                            fontFamily: 'monospace',
                            fontSize: 12,
                            textAlign: 'left',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <div>{`rtmp://${inst.publicIp}:1935/live/8k`}</div>
                          <div style={{ marginTop: 2 }}>{`srt://${inst.publicIp}:10080`}</div>
                          {/* Elastic IP picker: the rig publishes to a FIXED ip,
                              so the EIP follows the fresh instance of each event.
                              AllowReassociation on the backend = the usual
                              console "associate + force reassociation". */}
                          {advancedMode && Array.isArray(eips) && (
                            <div style={{ marginTop: 4, display: 'flex', gap: 4, alignItems: 'center' }}
                                 onClick={e => e.stopPropagation()}>
                              <select
                                value={eipSel[inst.instanceId] || ''}
                                onChange={e => setEipSel(prev => ({ ...prev, [inst.instanceId]: e.target.value }))}
                                style={{ fontSize: 11, padding: 2, background: '#222', color: '#fff', border: '1px solid #555', borderRadius: 4, maxWidth: 170 }}
                              >
                                <option value="">— Elastic IP —</option>
                                {/* EIPs are region-scoped: only offer the pool of the
                                    instance's region (cross-region associate fails).
                                    No region info (older backend) → show everything. */}
                                {eips
                                  .filter(a => !a.region || !inst.region || a.region === inst.region)
                                  .map(a => (
                                  <option key={a.allocationId} value={a.allocationId}>
                                    {a.name ? `${a.name} — ${a.publicIp}` : a.publicIp}
                                    {a.instanceId === inst.instanceId ? ' (this cam)'
                                      : a.instanceId ? ` (in use: ${a.instanceName})` : ' (free)'}
                                  </option>
                                ))}
                              </select>
                              <button
                                disabled={eipBusy || !eipSel[inst.instanceId]}
                                onClick={() => associateEip(inst)}
                                style={{
                                  fontSize: 11, padding: '2px 8px', border: 'none', borderRadius: 4,
                                  background: eipBusy || !eipSel[inst.instanceId] ? '#9e9e9e' : '#9a51e2',
                                  color: '#fff', cursor: eipBusy || !eipSel[inst.instanceId] ? 'not-allowed' : 'pointer',
                                }}
                              >
                                Assign
                              </button>
                            </div>
                          )}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          <Badge
                            ok={!!rtmpOk[inst.instanceId]}
                            on={COLORS.ok}
                            off={COLORS.err}
                            labelOk="OK"
                            labelOff="No"
                          />
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {(() => {
                            // Straight from rtmp-monitor.json's ingest.active — the
                            // slate/forced-source indirection is gone with the proxy.
                            const badge = <Badge ok={!!ingestOk[inst.instanceId]} on={COLORS.ok} off={COLORS.err} labelOk="OK" labelOff="No" />;
                            const kb = ingestKbps?.[inst.instanceId];
                            // kbps hangs below via absolute positioning so the row
                            // keeps its height and the badge stays aligned with its
                            // neighbor columns.
                            return (
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                {badge}
                                {kb != null && kb > 0 && (
                                  <span style={{
                                    position: 'absolute', top: 'calc(100% + 3px)', left: '50%',
                                    transform: 'translateX(-50%)', fontSize: 11,
                                    color: '#888', lineHeight: 1, whiteSpace: 'nowrap',
                                  }}>
                                    {(kb / 1000).toFixed(1)} Mb
                                  </span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                            <span style={styles.badge(encoderStatus.color)}>{encoderStatus.label}</span>
                            {eventStarted && encoderStatus.label === 'Stopped' && (
                              <button
                                style={miniIconBtn(COLORS.ok)}
                                data-mpdbtn
                                title={`Start encoding on ${inst.camName}`}
                                onClick={e => { e.stopPropagation(); startSingleCamEncoding(evt, inst); }}
                              >
                                ▶
                              </button>
                            )}
                            {eventStarted && encoderStatus.label === 'Running' && (
                              <button
                                style={miniIconBtn(COLORS.err)}
                                data-mpdbtn
                                title={`Stop encoding on ${inst.camName} (removes its track from the manifest; the rest of the event keeps running). Restart with ▶ — e.g. after changing its Signal offset.`}
                                onClick={e => {
                                  e.stopPropagation();
                                  if (window.confirm(`Stop encoding on ${inst.camName}?\n\nIts track is removed from the manifest while stopped; the other cameras are untouched. Restart it with ▶ (it rejoins the event grid).`)) {
                                    stopSingleCamEncoding(evt, inst);
                                  }
                                }}
                              >
                                ⏹
                              </button>
                            )}
            {/* Reboot THIS server only (its EC2 instance) — for a single
                                stuck/broken box, sin tocar las demás. Solo en modo
                                avanzado (reiniciar una VM no es acción de Studio). */}
                            {advancedMode && (
                            <button
                              style={{ ...miniIconBtn('#607d8b'), width: 22, height: 22, fontSize: 12 }}
                              data-mpdbtn
                              title={`Reboot only ${inst.camName} (full EC2 reboot: SRS + controller + encoder on that server; others untouched; ~30s)`}
                              onClick={e => { e.stopPropagation(); cfgRebootOne(evt, inst); }}
                            >
                              ⟳
                            </button>
                            )}
                          </div>
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {encoderStatus.label === 'Stopped'
                            ? <span style={styles.badge(COLORS.idle)}>-</span>
                            : renderSpeedPill(speedMap[inst.instanceId])}
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          <button
                            style={{
                              ...styles.badge('#2196F3'),
                              border: 'none',
                              cursor: 'pointer',
                              fontSize: 14,
                            }}
                            data-mpdbtn
                            title="Open camera monitor in popup"
                            onClick={e => {
                              e.stopPropagation();
                              const CLOUDFRONT_BASE = 'https://d10tner3hocpa9.cloudfront.net';
                              const camPath = `${evt}/${inst.camName}`;
                              const cameraData = { name: inst.camName, path: camPath, instanceId: inst.instanceId };
                              // Every cam of the event: the popup shows them as tabs
                              const camsData = camsOnly.map(c => ({
                                name: c.camName,
                                path: `${evt}/${c.camName}`,
                                instanceId: c.instanceId,
                              }));
                              const width = 760;
                              const height = 860;
                              const left = (window.screen.width - width) / 2;
                              const top = (window.screen.height - height) / 2;
                              const popupUrl = `/camera-monitor-popup?camera=${encodeURIComponent(JSON.stringify(cameraData))}&baseUrl=${encodeURIComponent(CLOUDFRONT_BASE)}&instanceId=${encodeURIComponent(inst.instanceId)}&cams=${encodeURIComponent(JSON.stringify(camsData))}`;
                              // One window per event (not per cam): clicking another cam's
                              // button reuses the window and just lands on that cam's tab.
                              window.open(popupUrl, `camera-monitor-${evt}`, `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`);
                            }}
                          >
                            📊
                          </button>
                        </td>
                        <td style={{ borderBottom: '1px solid #f5f5f5', padding: '4px 6px', textAlign: 'center' }}>
                          {(() => {
                            const role = detectRoleFromInst(inst);
                            const camName = inst.camName || '';
                            const vState = getFragEnabled(evt, camName, 'video');
                            const aState = getFragEnabled(evt, camName, 'audio');
                            const hasAnyFrag = vState !== undefined || aState !== undefined;

                            if (!inMpd && !hasAnyFrag) {
                              return (
                                <button
                                  style={miniIconBtn(COLORS.ok)}
                                  data-mpdbtn
                                  title="Add this cam to the event"
                                  onClick={e => { e.stopPropagation(); handleAddCam(evt, inst, masterInst, manifestText[evt] || ''); }}
                                >
                                  +
                                </button>
                              );
                            }

                            /* Studio mode: simple +/− only */
                            if (!advancedMode) {
                              return (
                                <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                                  <span style={{ color: COLORS.ok, fontSize: 13, fontWeight: 'bold' }} title="In manifest">✓</span>
                                  <button
                                    style={{ ...miniIconBtn(COLORS.err), width: 24, height: 24, fontSize: 14, borderRadius: 4 }}
                                    data-mpdbtn
                                    title="Remove this cam from the event"
                                    onClick={e => { e.stopPropagation(); handleRemoveCam(evt, inst, masterInst, manifestText[evt] || ''); }}
                                  >
                                    −
                                  </button>
                                </div>
                              );
                            }

                            /* Advanced mode: V/A toggles + remove */
                            const toggleBtnStyle = (on, color) => ({
                              ...styles.badge(color || (on ? COLORS.ok : COLORS.err)),
                              cursor: 'pointer',
                              border: 'none',
                              fontSize: 11,
                              minWidth: 44,
                              opacity: on ? 1 : 0.6,
                            });

                            return (
                              <div style={{ display: 'flex', gap: 3, justifyContent: 'center', alignItems: 'center' }}>
                                {role === 'video' && vState !== undefined && (
                                  <button
                                    style={toggleBtnStyle(vState)}
                                    data-mpdbtn
                                    title={`${vState ? 'Disable' : 'Enable'} video for ${camName}`}
                                    onClick={e => { e.stopPropagation(); handleToggleFragment(evt, camName, 'video', !vState, masterInst); }}
                                  >
                                    video
                                  </button>
                                )}
                                {aState !== undefined && (
                                  <button
                                    style={toggleBtnStyle(aState)}
                                    data-mpdbtn
                                    title={`${aState ? 'Disable' : 'Enable'} audio for ${camName}`}
                                    onClick={e => { e.stopPropagation(); handleToggleFragment(evt, camName, 'audio', !aState, masterInst); }}
                                  >
                                    audio
                                  </button>
                                )}
                                <button
                                  style={toggleBtnStyle(false, COLORS.err)}
                                  data-mpdbtn
                                  title="Remove this cam from the event"
                                  onClick={e => { e.stopPropagation(); handleRemoveCam(evt, inst, masterInst, manifestText[evt] || ''); }}
                                >
                                  −
                                </button>
                              </div>
                            );
                          })()}
                        </td>
                      </tr>
                      {/* Advanced mode: expandable detail panel */}
                      {isExpanded && (
                        <tr>
                          <td colSpan={totalCols} style={{ padding: 0, borderBottom: '2px solid #9a51e2' }}>
                            <div style={{ background: '#1a1a2e', padding: '12px 16px' }}>
                              {/* Tabs */}
                              <div style={{ display: 'flex', gap: 0, marginBottom: 12, borderBottom: '1px solid #444' }}>
                                {['preset', 'delay', 'logs'].map(tab => (
                                  <button
                                    key={tab}
                                    onClick={e => { e.stopPropagation(); setExpandedTab(tab); }}
                                    style={{
                                      padding: '8px 20px',
                                      background: expandedTab === tab ? '#9a51e2' : 'transparent',
                                      color: '#fff',
                                      border: 'none',
                                      borderBottom: expandedTab === tab ? '2px solid #fff' : '2px solid transparent',
                                      cursor: 'pointer',
                                      fontWeight: expandedTab === tab ? 600 : 400,
                                      fontSize: 13,
                                      textTransform: 'capitalize',
                                    }}
                                  >
                                    {tab}
                                  </button>
                                ))}
                              </div>

                              {/* Tab: Preset */}
                              {expandedTab === 'preset' && (() => {
                                const role = detectRoleFromInst(inst);
                                const filtered = presets.filter(p => !p.role || p.role === role);
                                const selPreset = filtered.find(p => p._id === advPresetId) || null;
                                return (
                                  <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                      <label style={{ color: '#9a51e2', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>Preset:</label>
                                      <select
                                        value={advPresetId}
                                        onClick={e => e.stopPropagation()}
                                        onChange={e => {
                                          const id = e.target.value;
                                          setAdvPresetId(id);
                                          const p = filtered.find(x => x._id === id);
                                          if (p) setEditCmd(p.command || '');
                                        }}
                                        style={{ flex: 1, padding: 6, borderRadius: 6, background: '#181828', color: '#fff', border: '1px solid #555', fontSize: 12 }}
                                      >
                                        <option value="">-- Select --</option>
                                        {filtered.map(p => (
                                          <option key={p._id} value={p._id}>{p.name}{p.isDefault ? ' (default)' : ''}</option>
                                        ))}
                                      </select>
                                      <button
                                        onClick={async e => {
                                          e.stopPropagation();
                                          if (!selPreset) return;
                                          try {
                                            await api.saveEventCmd(inst.eventName || evt, { instanceId: inst.instanceId, command: (selPreset.command || '').trim(), role });
                                            await api.updateNodeScript(inst.instanceId, selPreset.command || '');
                                            setEditCmd(selPreset.command || '');
                                            setMessage?.(`Preset "${selPreset.name}" applied to ${inst.camName}`);
                                          } catch (err) { setMessage?.(`Apply error: ${err.message}`); }
                                        }}
                                        disabled={!selPreset}
                                        style={{ padding: '5px 14px', background: selPreset ? '#4caf50' : '#555', color: '#fff', border: 'none', borderRadius: 6, cursor: selPreset ? 'pointer' : 'default', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' }}
                                      >Apply</button>
                                    </div>
                                    {selPreset ? (
                                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#181828', borderRadius: 6, border: '1px solid #444', fontSize: 11 }}>
                                        <div style={{ color: '#9a51e2', fontWeight: 600 }}>{selPreset.name}{selPreset.isDefault ? ' (default)' : ''}</div>
                                        {selPreset.description && <div style={{ color: '#aaa', marginTop: 4 }}>{selPreset.description}</div>}
                                      </div>
                                    ) : cmdFromDb[inst.instanceId] ? (
                                      <div style={{ marginTop: 8, padding: '8px 10px', background: '#181828', borderRadius: 6, border: '1px solid #555', fontSize: 11 }}>
                                        <div style={{ color: '#ff9800', fontWeight: 600 }}>Custom command (no preset match)</div>
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })()}

                              {/* Tab: Command */}
                              {/* Tab: Logs */}
                              {/* Tab: Delay */}
                              {expandedTab === 'delay' && (
                                <div>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                    <label style={{ color: '#9a51e2', fontWeight: 500, fontSize: 12, whiteSpace: 'nowrap' }}>Content Offset (seconds):</label>
                                    <input
                                      type="number"
                                      step="0.5"
                                      placeholder="0"
                                      value={contentOffsets[(inst.camName || '').toLowerCase()] ?? ''}
                                      onChange={e => {
                                        const val = e.target.value;
                                        updateContentOffset(evt, inst.camName, val === '' ? 0 : parseFloat(val) || 0);
                                      }}
                                      onClick={e => e.stopPropagation()}
                                      style={{
                                        width: 80,
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        background: '#181828',
                                        color: '#fff',
                                        border: '1px solid #555',
                                        fontSize: 13,
                                        textAlign: 'center',
                                      }}
                                    />
                                    <span style={{ color: '#888', fontSize: 11 }}>positive = delay this camera &nbsp;|&nbsp; negative = advance</span>
                                  </div>
                                  <p style={{ color: '#666', fontSize: 11, margin: '8px 0 0' }}>
                                    Added to the auto-calculated PTO on next MPD rebuild. Click "Rebuild MPD" to apply.
                                    <br />SRT ingest has ~3s less latency than RTMP. If mixing protocols in the same event, set +3.0s (or more) on SRT cameras to align with RTMP.
                                  </p>
                                </div>
                              )}

                              {expandedTab === 'logs' && (() => {
                                const fetchLog = async () => {
                                  try {
                                    const res = await authFetch(`${API_BASE}/event-wizard/proxy/${inst.instanceId}/ffmpeg/log`);
                                    const text = await res.text();
                                    setLogText(text);
                                  } catch { setLogText('Log fetch error'); }
                                };
                                if (!logText) fetchLog();
                                return (
                                  <div>
                                    <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                                      <button
                                        onClick={e => { e.stopPropagation(); fetchLog(); }}
                                        style={{ padding: '5px 14px', background: '#607d8b', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                                      >Refresh Log</button>
                                    </div>
                                    <pre
                                      onClick={e => e.stopPropagation()}
                                      style={{ background: '#181828', border: '1px solid #555', borderRadius: 6, padding: 10, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 11, color: '#e0e0e0', maxHeight: 300, overflowY: 'auto', margin: 0 }}
                                    >{logText || 'Loading...'}</pre>
                                  </div>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                    );
                  })}
                  {!camsOnly.length &&
                    pendingForEvt.filter((p) => (p.role || '').toLowerCase() !== 'master').length ===
                      0 && (
                      <tr>
                        <td colSpan={eventStarted ? 8 : 9} style={{ padding: '4px 6px', color: '#999' }}>
                          No cameras
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>

            {/* MPD URL (studio mode — simple display only). Shows the REAL live
                URL of the current session (manifest_<version>.mpd). Until the
                event is started there is no live manifest, so no URL is shown. */}
            <div style={{ margin: '12px 0' }}>
              {(liveByEvt[evt] && mfVersionByEvt[evt]) ? (() => {
                const liveUrl = `https://d10tner3hocpa9.cloudfront.net/${evt}/master/manifest_${mfVersionByEvt[evt]}.mpd`;
                return (
                  <p>
                    <strong>Event MPD:</strong>{' '}
                    <a href={liveUrl} target="_blank" rel="noopener noreferrer" style={{ color: '#9a51e2' }}>
                      {liveUrl}
                    </a>
                  </p>
                );
              })() : (
                <p style={{ color: '#999' }}>
                  <strong>Event MPD:</strong> —
                </p>
              )}
            </div>

            {/* Camera monitor / sync panel (GET /monitor per cam, IR-198) */}
            <div style={{ margin: '12px 0', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                onClick={() => (openMonitorEvt === evt ? setOpenMonitorEvt(null) : fetchMonitor(evt, list))}
                disabled={monitorLoadingEvt === evt}
                style={driftBtn(openMonitorEvt === evt ? '#9e9e9e' : COLORS.neutral, monitorLoadingEvt !== evt)}
                title="Clock, alignment, recovery and continuity of every camera of this event"
              >
                {monitorLoadingEvt === evt ? 'Loading…' : openMonitorEvt === evt ? 'Hide sync' : '📊 Camera sync'}
              </button>
              <button
                onClick={() => (openTrackOrderEvt === evt ? setOpenTrackOrderEvt(null) : fetchTrackOrder(evt))}
                disabled={trackOrderBusyEvt === evt}
                style={driftBtn(openTrackOrderEvt === evt ? '#9e9e9e' : COLORS.neutral, trackOrderBusyEvt !== evt)}
                title="Reorder cameras and audios in the manifest — the first one is the viewer default"
              >
                {trackOrderBusyEvt === evt ? 'Loading…' : openTrackOrderEvt === evt ? 'Hide order' : '🔀 Track order'}
              </button>
              {/* ⚡ LL toggle: solo en Advanced (escape hatch). Operativa normal =
                  chunked SIEMPRE ON (default) y se juega con el Target latency; el
                  toggle queda para el caso raro de un evento no-chunked o debug. */}
              {advancedMode && (
              <button
                onClick={() => toggleLowLatency(evt)}
                style={driftBtn(llByEvt[evt] === true ? '#9a51e2' : COLORS.neutral)}
                title="⚡ LL-DASH chunked: rebuild with availabilityTimeOffset=0.8 + LL player preset in Dash Settings. Requires the chunked chain live (LL ffmpeg preset, ingest-receiver, nginx fallback). Normalmente SIEMPRE ON; solo apagar para un evento no-chunked o debug."
              >
                {llByEvt[evt] == null ? '⚡ LL: …' : llByEvt[evt] ? '⚡ LL: ON' : '⚡ LL: off'}
              </button>
              )}
            </div>

            {/* Track order panel (IR-196) */}
            {openTrackOrderEvt === evt && trackOrderByEvent[evt] && (() => {
              const cur = trackOrderByEvent[evt];
              const busy = trackOrderBusyEvt === evt;
              const listBox = (kind, title, items) => (
                <div style={{ minWidth: 260 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{title}</div>
                  {!items.length && <div style={{ color: '#999', fontSize: 12 }}>None in manifest</div>}
                  {items.map((item, i) => (
                    <div key={item.camName} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 0' }}>
                      <span style={{ width: 18, textAlign: 'right', color: i === 0 ? COLORS.ok : '#888', fontSize: 12 }}>{i + 1}.</span>
                      <span style={{ flex: 1, fontSize: 13 }}>
                        <span style={{ fontFamily: 'monospace' }}>{item.camName}</span>
                        {item.label && <span style={{ color: '#bbb' }}> — {item.label}</span>}
                      </span>
                      <button
                        disabled={busy || i === 0}
                        onClick={() => moveTrack(evt, kind, i, -1)}
                        style={{ ...miniIconBtn('#607d8b'), width: 24, height: 24, fontSize: 13, opacity: i === 0 ? 0.3 : 1 }}
                        title="Move up"
                      >↑</button>
                      <button
                        disabled={busy || i === items.length - 1}
                        onClick={() => moveTrack(evt, kind, i, +1)}
                        style={{ ...miniIconBtn('#607d8b'), width: 24, height: 24, fontSize: 13, opacity: i === items.length - 1 ? 0.3 : 1 }}
                        title="Move down"
                      >↓</button>
                    </div>
                  ))}
                </div>
              );
              return (
                <div style={{ margin: '12px 0', borderTop: '1px dashed #555', paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong>Track order</strong>
                    <button
                      onClick={() => setOpenTrackOrderEvt(null)}
                      style={{ marginLeft: 'auto', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                    >Close</button>
                  </div>
                  <div style={{ display: 'flex', gap: 32, flexWrap: 'wrap' }}>
                    {listBox('videos', 'Cameras', cur.videos)}
                    {listBox('audios', 'Audios', cur.audios)}
                  </div>
                  <div style={{ marginTop: 10, display: 'flex', gap: 8, alignItems: 'center' }}>
                    <button
                      disabled={busy || !cur.dirty}
                      onClick={() => applyTrackOrder(evt)}
                      style={driftBtn(COLORS.warn, !busy && cur.dirty)}
                    >{busy ? 'Applying…' : 'Apply & rebuild manifest'}</button>
                    {cur.dirty && !busy && <span style={{ fontSize: 12, color: COLORS.warn }}>Unsaved changes</span>}
                  </div>
                </div>
              );
            })()}

            {openMonitorEvt === evt && monitorByEvent[evt] && (() => {
              const { rows = [], loadedAt } = monitorByEvent[evt];
              const { usable, refEpoch, epochSpreadMs, untrusted } = monitorAnalysis(rows);
              const th = { borderBottom: '1px solid #666', padding: '7px 18px 7px 0', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 600, fontSize: 13, color: '#aaa' };
              const td = { borderBottom: '1px solid #3a3a3a', padding: '7px 18px 7px 0', textAlign: 'left', whiteSpace: 'nowrap', fontSize: 14.5 };
              const mono = { fontFamily: 'monospace', fontSize: 14 };
              const stateColor = (s) =>
                s === 'RUNNING' ? COLORS.ok
                : (s === 'STOPPED' || s === 'IDLE') ? COLORS.idle
                : COLORS.warn; // BACKOFF / WAITING_STABLE / WAITING_SIGNAL
              const stateLabel = (s) => (s ? String(s).toLowerCase().replace(/_/g, ' ') : '—');
              const fmtClock = (ms) => new Date(ms).toISOString().slice(11, 23);

              return (
                <div style={{ marginTop: 12, borderTop: '1px dashed #ddd', paddingTop: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, flexWrap: 'wrap' }}>
                    <strong>Camera sync</strong>
                    {usable.length > 1 && (
                      <span
                        style={styles.badge(epochSpreadMs === 0 ? COLORS.ok : COLORS.err)}
                        title="Every camera of the event must anchor on the same event epoch (the manifest AST). Any difference is the desynchronisation, in milliseconds."
                      >
                        {epochSpreadMs === 0 ? 'clocks aligned' : `clocks differ ${(epochSpreadMs / 1000).toFixed(3)}s`}
                      </span>
                    )}
                    {untrusted.length > 0 && (
                      <span
                        style={styles.badge(COLORS.err)}
                        title="These controllers anchored t=0 with their own clock: the platform epoch never reached their /ffmpeg/go. Their recoveries land shifted."
                      >
                        local clock: {untrusted.map(r => r.camName).join(', ')}
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: '#666' }}>
                      updated {new Date(loadedAt).toLocaleTimeString()} · every {MONITOR_POLL_MS / 1000}s
                    </span>
                    <button
                      onClick={() => setOpenMonitorEvt(null)}
                      style={{ marginLeft: 'auto', background: '#9e9e9e', color: '#fff', border: 'none', borderRadius: 4, padding: '4px 8px', cursor: 'pointer' }}
                    >Close</button>
                  </div>

                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ borderCollapse: 'collapse' }}>
                      <thead>
                        <tr>
                          <th style={th}>Cam</th>
                          <th style={th} title="Event epoch (t=0) this camera is anchored on — UTC; red if it differs from the others">Clock (t=0)</th>
                          <th style={th} title="producing − expected segment. + = ahead of the event clock = older picture = extra latency. 0/−1 is the normal granularity.">Offset</th>
                          <th style={th} title="Declared input-signal latency (ingest profile) applied as a slot shift at the camera's start: its content is delayed this many seconds to line up with slower transports. Already discounted in Offset.">Signal delay</th>
                          <th style={th} title="Per-camera supervision: running / backoff / waiting stable (bitrate settling) / waiting signal / stopped">Recovery</th>
                          <th style={th} title="Encoder deaths recovered during this event">Drops</th>
                          <th style={th} title="Seconds of video missing (filled in the VOD by phase 3)">Gaps</th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map(r => {
                          const off = r.offsetSegments;
                          const offColor = off == null ? '#999' : Math.abs(off) >= OFFSET_WARN_SEGS ? COLORS.warn : COLORS.ok;
                          // Stopped cams keep serving their LAST event's epoch — show it
                          // dimmed and without delta: it is not part of this comparison.
                          const isActive = !['STOPPED', 'IDLE'].includes(r.recoveryState);
                          const deltaMs = (r.ok && isActive && r.epochMs != null && refEpoch != null) ? r.epochMs - refEpoch : null;
                          const gapCount = (r.gaps || []).length;
                          return (
                            <tr key={r.instanceId}>
                              <td style={{ ...td, fontWeight: 600 }}>{r.camName}</td>
                              {r.ok ? (
                                <>
                                  <td
                                    style={{ ...td, ...mono, color: !isActive ? '#666' : (!r.epochTrusted || (deltaMs != null && deltaMs !== 0)) ? COLORS.err : undefined }}
                                    title={!isActive ? 'stopped — last event\'s epoch, excluded from the comparison' : (r.epochSource || '')}
                                  >
                                    {r.epochMs == null ? '—'
                                      : !isActive ? fmtClock(r.epochMs)
                                      : !r.epochTrusted ? 'local clock'
                                      : deltaMs ? `${fmtClock(r.epochMs)} (+${deltaMs}ms)` : fmtClock(r.epochMs)}
                                  </td>
                                  <td style={{ ...td, ...mono, color: offColor }}>
                                    {off == null ? '—' : off === 0 ? 'aligned' : `${off > 0 ? '+' : ''}${off} seg`}
                                  </td>
                                  <td style={{ ...td, ...mono, color: r.signalOffsetSec ? '#9a51e2' : '#888' }}>
                                    {r.signalOffsetSec ? `+${r.signalOffsetSec}s` : '—'}
                                  </td>
                                  <td style={{ ...td, color: stateColor(r.recoveryState) }}>{stateLabel(r.recoveryState)}</td>
                                  <td style={{ ...td, ...mono, color: r.deaths ? COLORS.warn : '#888' }}>{r.deaths || 0}</td>
                                  <td
                                    style={{ ...td, color: r.missingSegs ? COLORS.warn : '#888' }}
                                    title={(r.gaps || []).map(g => `${g.from}–${g.to} (${g.count}s)`).join('\n') || 'no gaps'}
                                  >
                                    {r.missingSegs ? `${r.missingSegs}s (${gapCount} gap${gapCount === 1 ? '' : 's'})` : 'none'}
                                  </td>
                                </>
                              ) : (
                                <td colSpan={6} style={{ ...td, color: COLORS.err }}>unreachable{r.error ? ` — ${r.error}` : ''}</td>
                              )}
                            </tr>
                          );
                        })}
                        {!rows.length && <tr><td colSpan={7} style={{ ...td, color: '#999' }}>No cameras</td></tr>}
                      </tbody>
                    </table>
                  </div>

                  {(() => {
                    // Align content offsets from the monitor's own measurement.
                    // offsetSegments differences between cameras ARE the content
                    // misalignment in seconds (validated 2026-10-13: −3 vs −1
                    // measured as exactly 2s of player latency difference). The
                    // reference is the camera with the HIGHEST offset (oldest
                    // content = most latency): every other camera is DELAYED to
                    // meet it — a camera can never be advanced past its own live
                    // edge. Writes absolute per-cam content offsets, so the
                    // action is idempotent; manual offsets are overwritten.
                    const usableOff = rows.filter(r => r.ok && r.offsetSegments != null);
                    if (usableOff.length < 2) return null;
                    const maxOff = Math.max(...usableOff.map(r => r.offsetSegments));
                    const byCam = {};
                    for (const r of usableOff) byCam[(r.camName || '').toLowerCase()] = maxOff - r.offsetSegments;
                    const changes = Object.entries(byCam).filter(([k, v]) => (Number(contentOffsets[k] ?? 0) || 0) !== v);
                    const masterInst = Object.values(grouped).flat()
                      .find(i => (i.role || '').toLowerCase() === 'master' && i.eventName === evt);
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                        <button
                          disabled={!changes.length}
                          title={changes.length
                            ? `Delay ${changes.map(([k, v]) => `${k} to ${v}s`).join(', ')} so every camera shows content from the same instant`
                            : 'Offsets already match the measured misalignment'}
                          onClick={async () => {
                            const detail = changes.map(([k, v]) => `  ${k}: ${(Number(contentOffsets[k] ?? 0) || 0)}s → ${v}s`).join('\n');
                            if (!confirmDanger(
                              `Set the content offset of ${changes.length} camera(s) to the misalignment measured by /monitor and rebuild the manifest?\n\n${detail}\n\n` +
                              `Viewers reposition on their next manifest refresh. Wait for a stable reading before applying (the measurement moves in whole segments).`
                            )) return;
                            try {
                              setMessage?.('Aligning content offsets…');
                              for (const [k, v] of changes) {
                                const row = usableOff.find(r => (r.camName || '').toLowerCase() === k);
                                setContentOffsets(prev => ({ ...prev, [k]: v }));
                                await api.saveContentOffset(evt, row?.camName || k, v);
                              }
                              // forcePtoZero: the monitor offsets ARE the alignment
                              // source — letting the birthtime auto PTO back in on
                              // this rebuild would correct the same thing twice.
                              await api.rebuildMpd(evt, [], masterInst?.instanceId, { forcePtoZero: true });
                              setMessage?.(`Offsets aligned on ${changes.length} camera(s) and manifest rebuilt.`);
                              await fetchMonitor(evt, list, { silent: true });
                            } catch (e) {
                              setMessage?.(`Align failed: ${e.response?.data?.message || e.message}`);
                            }
                          }}
                          style={driftBtn(COLORS.warn, !!changes.length)}
                        >
                          {changes.length ? `Align content offsets (${changes.map(([k, v]) => `${k} +${v}s`).join(', ')})` : 'Offsets aligned'}
                        </button>
                        <span style={{ fontSize: 12, color: '#888', maxWidth: 520 }}>
                          Patches THIS session's start misalignment by delaying the fresher cameras.
                          The start flow already does this automatically (auto-align phase); this button
                          is for mid-event corrections.
                        </span>
                      </div>
                    );
                  })()}

                  <span style={{ display: 'block', marginTop: 10, fontSize: 12, color: '#888', maxWidth: 720 }}>
                    A dropped camera relaunches itself on the shared timeline
                    (backoff 2/5/10/10/10s, then waits for the signal and for its bitrate to
                    settle — waiting stable costs ~10-35s on purpose: relaunching into the
                    publisher's reconnect burst is what used to desynchronise cameras for good).
                    Gaps stay as holes in the live window and are filled with slate in the VOD.
                  </span>
                </div>
              );
            })()}

            {/* NEW: First segment results panel */}
            {openFirstSegEvt === evt && firstSegByEvent[evt] && (
              <div style={{ marginTop: 12, borderTop: '1px dashed #ddd', paddingTop: 12 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:8 }}>
                  <strong>First segment creation times</strong>
                  <span style={{ fontSize:12, color:'#666' }}>
                    (loaded {new Date(firstSegByEvent[evt].loadedAt).toLocaleString()})
                  </span>
                  <button
                    onClick={() => setOpenFirstSegEvt(null)}
                    style={{ marginLeft:'auto', background:'#9e9e9e', color:'#fff', border:'none', borderRadius:4, padding:'4px 8px', cursor:'pointer' }}
                  >
                    Close
                  </button>
                </div>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse' }}>
                    <thead>
                      <tr style={{ textAlign:'left' }}>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Cam</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Source</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Created (ISO)</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>File</th>
                        <th style={{ borderBottom:'1px solid #eee', padding:'8px' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(firstSegByEvent[evt].rows || []).map(row => (
                        <tr key={row.instanceId} style={{ fontSize:'0.95em' }}>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px' }}>{row.camName}</td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px', textTransform:'capitalize' }}>{row.source}</td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px', fontFamily:'monospace' }}>
                            {row.createdAtIso || <span style={{ color:'#999' }}>—</span>}
                          </td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px' }}>
                            <a href={row.url} target="_blank" rel="noopener noreferrer" style={{ color: '#9a51e2', textDecoration: 'underline' }}>{row.url}</a>
                          </td>
                          <td style={{ borderBottom:'1px solid #f5f5f5', padding:'8px' }}>
                            {row.ok
                              ? <span style={styles.badge(COLORS.ok)}>OK</span>
                              : <span style={styles.badge(COLORS.err)} title={row.error || ''}>Error</span>}
                          </td>
                        </tr>
                      ))}
                      {(!firstSegByEvent[evt].rows || firstSegByEvent[evt].rows.length === 0) && (
                        <tr>
                          <td colSpan={5} style={{ padding:'8px', color:'#999' }}>No data</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </div>
        );
      })}
    </div>
  );
}
